/**
 * Message catalog. Keys are stable; the three vernaculars are values.
 * Vocabulary follows the interface map: EN (US) / ES (LAM) / PT-BR.
 *
 * A missing key falls back to the English string, then to the key itself,
 * so a partially-translated catalog never renders a blank.
 */
import type { Locale } from "./locales";

export type MessageKey =
  // shell chrome (settings cogwheel + well switcher live in the left nav)
  | "app.name"
  | "topbar.wellSwitcher"
  | "topbar.theme"
  | "topbar.theme.dark"
  | "topbar.theme.light"
  | "topbar.theme.auto"
  | "topbar.locale"
  | "topbar.lineage"
  | "topbar.lineage.adminOnly"
  | "topbar.lineage.on"
  | "topbar.lineage.off"
  | "settings.title"
  | "settings.open"
  | "settings.tenant"
  | "settings.units"
  | "settings.units.auto"
  | "settings.units.hint"
  | "settings.currency"
  | "settings.currency.native"
  | "nav.collapse"
  | "nav.expand"
  | "well.switcher.title"
  // nav (sitemap)
  | "nav.wellExplorer"
  | "nav.wellRecord"
  | "nav.dailyReport"
  | "nav.timeNpt"
  | "nav.costAfe"
  | "nav.bha"
  | "nav.tubulars"
  | "nav.mud"
  | "nav.hse"
  | "nav.surveys"
  | "nav.pad"
  | "nav.inbox"
  | "nav.savedReports"
  | "nav.afe"
  // AFE management (§11)
  | "afe.title"
  | "afe.kpi.total"
  | "afe.kpi.committed"
  | "afe.kpi.accrued"
  | "afe.kpi.actual"
  | "afe.kpi.variance"
  | "afe.sequence.title"
  | "afe.sequence.accruedOf"
  | "afe.lines.title"
  | "afe.col.account"
  | "afe.col.description"
  | "afe.col.method"
  | "afe.col.treatment"
  | "afe.col.budget"
  | "afe.col.committed"
  | "afe.col.accrued"
  | "afe.col.actual"
  | "afe.col.variance"
  | "afe.col.status"
  | "afe.method.by_quantity"
  | "afe.method.lump"
  | "afe.method.pct_complete"
  | "afe.method.milestone"
  | "afe.owner.service"
  | "afe.scope.service"
  | "afe.scope.serviceHint"
  | "afe.devengado.term"
  | "afe.devengado.hint"
  | "afe.edit.method"
  | "afe.noLines"
  | "nav.planner"
  // planner / multi-track timeline (§13/§13a)
  | "planner.title"
  | "planner.subtitle"
  | "planner.level"
  | "planner.level.corporate"
  | "planner.level.regional"
  | "planner.level.local"
  | "planner.level.fleet"
  | "planner.level.pad"
  | "planner.level.well"
  | "planner.level.section"
  | "planner.zoom"
  | "planner.zoom.day"
  | "planner.zoom.week"
  | "planner.zoom.month"
  | "planner.zoom.quarter"
  | "planner.zoom.year"
  | "planner.autoZoom"
  | "planner.autoZoom.hint"
  | "planner.show"
  | "planner.pa.both"
  | "planner.pa.plan"
  | "planner.pa.actual"
  | "planner.legend.plan"
  | "planner.legend.actual"
  | "planner.legend.online"
  | "planner.legend.offline"
  | "planner.legend.npt"
  | "planner.legend.today"
  | "planner.cpeSpine"
  | "planner.levelLocked"
  | "planner.empty"
  | "nav.catalogAdmin"
  // catalog admin (preview) — read-only browse of Oxbo-M catalogs (§22/§23)
  | "catadmin.title"
  | "catadmin.subtitle"
  | "catadmin.previewNote"
  | "catadmin.adminOnly"
  | "catadmin.nav.aria"
  | "catadmin.nav.chrome"
  | "catadmin.nav.find"
  | "catadmin.nav.noMatch"
  | "catadmin.group.operations"
  | "catadmin.group.cost"
  | "catadmin.group.equipment"
  | "catadmin.group.fluids"
  | "catadmin.shape"
  | "catadmin.version"
  | "catadmin.valueCount"
  | "catadmin.seededFrom"
  | "catadmin.manage"
  | "catadmin.manageAria"
  | "catadmin.noValues"
  | "catadmin.col.code"
  | "catadmin.col.label"
  | "catadmin.col.parent"
  | "catadmin.col.aliases"
  | "catadmin.col.active"
  | "catadmin.on"
  | "catadmin.retired"
  | "catadmin.govnote"
  // well explorer
  | "explorer.title"
  | "explorer.subtitle"
  | "explorer.search"
  | "explorer.filter.field"
  | "explorer.filter.basin"
  | "explorer.filter.operator"
  | "explorer.filter.status"
  | "explorer.filter.all"
  | "explorer.col.well"
  | "explorer.col.uwi"
  | "explorer.col.field"
  | "explorer.col.basin"
  | "explorer.col.operator"
  | "explorer.col.purpose"
  | "explorer.col.status"
  | "explorer.col.td"
  | "explorer.map.placeholder"
  | "explorer.count"
  | "explorer.empty"
  | "explorer.clearFilters"
  // status values
  | "status.drilling"
  | "status.completion"
  | "status.producing"
  | "status.suspended"
  // report / event states + types
  | "state.open"
  | "state.closed"
  | "eventType.drilling"
  | "eventType.completion"
  | "eventType.workover"
  | "eventType.intervention"
  // well record hub
  | "well.notSelected"
  | "well.selectPrompt"
  | "well.header.uwi"
  | "well.header.api"
  | "well.header.field"
  | "well.header.operator"
  | "well.header.rig"
  | "well.header.formation"
  | "well.header.td"
  | "well.header.status"
  | "well.events"
  | "well.event.select"
  | "well.event.noEvents"
  | "well.doorway.dailyReport"
  | "well.doorway.costAfe"
  | "well.doorway.timeNpt"
  | "well.doorway.latestReport"
  | "well.doorway.spent"
  | "well.doorway.nptPct"
  | "well.doorway.open"
  // daily drilling report
  | "ddr.title"
  | "ddr.reportDate"
  | "ddr.day"
  | "ddr.selectReport"
  | "ddr.noReports"
  | "ddr.create"
  | "ddr.creating"
  | "ddr.save"
  | "ddr.saving"
  | "ddr.close"
  | "ddr.closed"
  | "ddr.carryOver"
  | "ddr.carryOverHint"
  | "ddr.computed"
  | "ddr.tab.general"
  | "ddr.tab.timeNpt"
  | "ddr.tab.cost"
  | "ddr.tab.personnel"
  | "ddr.tab.attachments"
  | "ddr.kpi.day"
  | "ddr.kpi.md"
  | "ddr.kpi.tvd"
  | "ddr.kpi.rop"
  | "ddr.kpi.pct"
  | "ddr.kpi.cost"
  | "ddr.summary"
  | "ddr.forecast"
  | "ddr.supervisor"
  | "ddr.field.mdCurrent"
  | "ddr.field.tvdCurrent"
  | "ddr.personnel.placeholder"
  | "ddr.attachments.placeholder"
  // time / NPT
  | "npt.title"
  | "npt.subtitle"
  | "npt.kpi.totalHours"
  | "npt.kpi.nptHours"
  | "npt.kpi.nptPct"
  | "npt.byCause"
  | "npt.col.cause"
  | "npt.col.hours"
  | "npt.col.share"
  | "npt.activityCodes"
  | "npt.noData"
  // cost / afe
  | "cost.title"
  | "cost.kpi.afeTotal"
  | "cost.kpi.actual"
  | "cost.kpi.variance"
  | "cost.kpi.pctSpent"
  | "cost.byPhase"
  | "cost.pivot"
  | "cost.pivot.vendor"
  | "cost.pivot.phase"
  | "cost.pivot.costClass"
  | "cost.col.phase"
  | "cost.col.afe"
  | "cost.col.actual"
  | "cost.col.variance"
  | "cost.col.variancePct"
  | "cost.legend.plan"
  | "cost.legend.actual"
  | "cost.legend.over"
  | "cost.col.key"
  | "cost.col.total"
  | "cost.total"
  | "cost.noData"
  // inbox (cross-well "Bandeja" feed, §3)
  | "inbox.title"
  | "inbox.subtitle"
  | "inbox.kpi.open"
  | "inbox.kpi.closed"
  | "inbox.kpi.loaded"
  | "inbox.kpi.wells"
  | "inbox.filter.state"
  | "inbox.filter.eventType"
  | "inbox.filter.well"
  | "inbox.filter.dateRange"
  | "inbox.filter.scope"
  | "inbox.filter.all"
  | "inbox.filter.allWells"
  | "inbox.state.open"
  | "inbox.state.closed"
  | "inbox.range.days"
  | "inbox.range.all"
  | "inbox.scope.all"
  | "inbox.scope.mine"
  | "inbox.col.state"
  | "inbox.col.well"
  | "inbox.col.eventType"
  | "inbox.col.date"
  | "inbox.col.supervisor"
  | "inbox.col.age"
  | "inbox.loadMore"
  | "inbox.clearFilters"
  | "inbox.refresh"
  | "inbox.empty"
  // saved views
  | "saved.title"
  | "saved.subtitle"
  | "saved.new"
  | "saved.search"
  | "saved.filter.all"
  | "saved.filter.mine"
  | "saved.filter.public"
  | "saved.col.name"
  | "saved.col.description"
  | "saved.col.opensIn"
  | "saved.col.opensIn.title"
  | "saved.col.visibility"
  | "saved.col.owner"
  | "saved.col.actions"
  | "saved.visibility.public"
  | "saved.visibility.private"
  | "saved.action.open"
  | "saved.action.share"
  | "saved.action.export"
  | "saved.action.delete"
  | "saved.empty"
  | "saved.empty.clear"
  | "saved.noData"
  | "saved.noDescription"
  | "saved.exportSoon"
  | "saved.confirmDelete"
  | "saved.dialog.title"
  | "saved.dialog.name"
  | "saved.dialog.namePlaceholder"
  | "saved.dialog.description"
  | "saved.dialog.descriptionPlaceholder"
  | "saved.dialog.visibility"
  | "saved.dialog.visibility.private"
  | "saved.dialog.visibility.public"
  | "saved.dialog.opensIn"
  | "saved.dialog.specNote"
  | "saved.dialog.noSpecHint"
  | "saved.dialog.cancel"
  | "saved.dialog.save"
  | "saved.dialog.saving"
  | "saved.target.inbox"
  | "saved.target.cost-afe"
  | "saved.target.time-npt"
  | "saved.target.surveys"
  | "saved.target.bha"
  | "saved.target.tubulars"
  | "saved.target.mud"
  | "saved.target.hse"
  // surveys (§5)
  | "surveys.title"
  | "surveys.kpi.currentMd"
  | "surveys.kpi.currentTvd"
  | "surveys.kpi.maxInc"
  | "surveys.kpi.maxDls"
  | "surveys.kpi.closure"
  | "surveys.unit.dls"
  | "surveys.col.md"
  | "surveys.col.inc"
  | "surveys.col.azi"
  | "surveys.col.tvd"
  | "surveys.col.ns"
  | "surveys.col.ew"
  | "surveys.col.dls"
  | "surveys.col.tool"
  | "surveys.col.date"
  | "surveys.col.calc"
  | "surveys.col.pending"
  | "surveys.dls.warn"
  | "surveys.dls.warnTitle"
  | "surveys.paste.title"
  | "surveys.paste.hint"
  | "surveys.paste.note"
  | "surveys.paste.placeholder"
  | "surveys.paste.action"
  | "surveys.stations.title"
  | "surveys.stations.hint"
  | "surveys.stations.empty"
  | "surveys.compute.note"
  | "surveys.plot.empty"
  | "surveys.plot.wellpath"
  | "surveys.plot.dls"
  | "surveys.plot.plan"
  | "surveys.plot.plan.sub"
  | "surveys.plot.plan.aria"
  | "surveys.plot.section"
  | "surveys.plot.section.sub"
  | "surveys.plot.section.aria"
  | "surveys.plot.dlsTitle"
  | "surveys.plot.dls.sub"
  | "surveys.plot.dls.aria"
  | "surveys.axis.easting"
  | "surveys.axis.northing"
  | "surveys.axis.vsection"
  | "surveys.axis.tvd"
  | "surveys.axis.md"
  | "surveys.axis.dls"
  // tubulars
  | "tub.title"
  | "tub.subtitle"
  | "tub.tab.strings"
  | "tub.tab.tally"
  | "tub.addString"
  | "tub.addJoints"
  | "tub.kpi.totalLength"
  | "tub.kpi.shoeMd"
  | "tub.kpi.shoeTvd"
  | "tub.kpi.jointsRun"
  | "tub.strings.caption"
  | "tub.col.string"
  | "tub.col.size"
  | "tub.col.grade"
  | "tub.col.connection"
  | "tub.col.shoeMd"
  | "tub.col.shoeTvd"
  | "tub.col.status"
  | "tub.status.running"
  | "tub.status.set"
  | "tub.status.planned"
  | "tub.carried"
  | "tub.tally.caption"
  | "tub.tally.for"
  | "tub.tally.pick"
  | "tub.col.jt"
  | "tub.col.length"
  | "tub.col.cum"
  | "tub.col.od"
  | "tub.col.serial"
  | "tub.tally.run"
  | "tub.tally.runningJoint"
  | "tub.schematic.title"
  | "tub.schematic.subtitle"
  | "tub.schematic.bitDepth"
  | "tub.schematic.openHole"
  | "tub.schematic.wellhead"
  | "tub.noData"
  | "tub.noStrings"
  | "tub.noTally"
  // bha / bit
  | "bha.title"
  | "bha.newRun"
  | "bha.kpi.rop"
  | "bha.kpi.interval"
  | "bha.kpi.bitHours"
  | "bha.kpi.lastDull"
  | "bha.runs"
  | "bha.col.run"
  | "bha.col.inMd"
  | "bha.col.outMd"
  | "bha.col.hours"
  | "bha.col.bit"
  | "bha.col.rop"
  | "bha.col.components"
  | "bha.inHole"
  | "bha.pooh"
  | "bha.runDetail"
  | "bha.components"
  | "bha.comp.type"
  | "bha.comp.od"
  | "bha.comp.length"
  | "bha.comp.vendor"
  | "bha.bit"
  | "bha.bit.size"
  | "bha.bit.jets"
  | "bha.bit.inOut"
  | "bha.bit.hours"
  | "bha.bit.tfa"
  | "bha.dull"
  | "bha.bitOps"
  | "bha.op.md"
  | "bha.op.rop"
  | "bha.op.wob"
  | "bha.op.rpm"
  | "bha.op.flow"
  | "bha.schematic"
  | "bha.ropVsDepth"
  | "bha.selectRun"
  | "bha.noData"
  | "bha.form.runNo"
  | "bha.form.bitType"
  | "bha.form.vendor"
  | "bha.form.save"
  // mud / fluids
  | "mud.title"
  | "mud.subtitle"
  | "mud.noData"
  | "mud.selectReport"
  | "mud.kpi.currentMw"
  | "mud.kpi.ecd"
  | "mud.kpi.dailyCost"
  | "mud.kpi.cumLosses"
  | "mud.section.properties"
  | "mud.section.properties.note"
  | "mud.section.products"
  | "mud.section.products.note"
  | "mud.section.volumes"
  | "mud.col.time"
  | "mud.col.depth"
  | "mud.col.mw"
  | "mud.col.pv"
  | "mud.col.yp"
  | "mud.col.ph"
  | "mud.col.chlorides"
  | "mud.col.product"
  | "mud.col.qty"
  | "mud.col.unit"
  | "mud.col.unitCost"
  | "mud.col.total"
  | "mud.vol.active"
  | "mud.vol.reserve"
  | "mud.vol.built"
  | "mud.vol.dumped"
  | "mud.vol.lossSurface"
  | "mud.vol.lossDownhole"
  | "mud.chart.trend"
  | "mud.chart.trend.sub"
  | "mud.chart.losses"
  | "mud.chart.cost"
  | "mud.axis.reportDate"
  | "mud.axis.value"
  | "mud.axis.losses"
  | "mud.axis.cost"
  | "mud.axis.product"
  | "mud.carried"
  | "mud.noChecks"
  | "mud.noProducts"
  | "mud.totalDailyCost"
  | "mud.mwWarn"
  | "hse.title"
  | "hse.subtitle"
  | "hse.kpi.daysSinceLti"
  | "hse.kpi.daysSinceLtiOperator"
  | "hse.kpi.observations"
  | "hse.kpi.nearMisses"
  | "hse.kpi.drills"
  | "hse.day.title"
  | "hse.day.wellControl"
  | "hse.day.bopTest"
  | "hse.day.koomey"
  | "hse.day.checks"
  | "hse.day.ridCards"
  | "hse.day.jsa"
  | "hse.day.toolbox"
  | "hse.day.drillsLogged"
  | "hse.day.people"
  | "hse.day.crewOnSite"
  | "hse.day.recordables"
  | "hse.day.firstAids"
  | "hse.day.narrative"
  | "hse.bop.pass"
  | "hse.bop.fail"
  | "hse.bop.na"
  | "hse.trail.title"
  | "hse.trail.date"
  | "hse.trail.bop"
  | "hse.trail.ridCards"
  | "hse.trail.jsa"
  | "hse.trail.toolbox"
  | "hse.trail.drills"
  | "hse.trail.recordables"
  | "hse.trail.observations"
  | "hse.yes"
  | "hse.no"
  | "hse.noData"
  // pad view (§12)
  | "pad.title"
  | "pad.wellCount"
  | "pad.kpi.totalCost"
  | "pad.kpi.sharedCost"
  | "pad.kpi.allocated"
  | "pad.kpi.padDays"
  | "pad.kpi.days"
  | "pad.kpi.rigUtil"
  | "pad.costs"
  | "pad.time"
  | "pad.sharedLines"
  | "pad.noSharedLines"
  | "pad.perWellRollup"
  | "pad.timeRollup"
  | "pad.viewSplit"
  | "pad.rigUtilNote"
  | "pad.noData"
  | "pad.noTimeline"
  | "pad.col.costClass"
  | "pad.col.description"
  | "pad.col.vendor"
  | "pad.col.amount"
  | "pad.col.allocMethod"
  | "pad.col.split"
  | "pad.col.well"
  | "pad.col.share"
  | "pad.col.allocated"
  | "pad.col.directCost"
  | "pad.col.allocatedShared"
  | "pad.col.total"
  | "pad.col.pctOfPad"
  | "pad.col.padTotal"
  | "pad.col.daysOnWell"
  | "pad.col.sequence"
  | "pad.alloc.equal"
  | "pad.alloc.wellCount"
  | "pad.alloc.footage"
  | "pad.alloc.rigDays"
  | "pad.alloc.afeWeight"
  | "pad.alloc.manualPct"
  | "pad.chart.costByWell"
  | "pad.chart.gantt"
  | "pad.chart.date"
  // common
  | "common.loading"
  | "common.error"
  | "common.retry"
  | "common.export"
  | "common.cancel"
  | "common.na"
  | "common.of";

type Catalog = Record<MessageKey, string>;

const en: Catalog = {
  "app.name": "Oxbo-Z",
  "topbar.wellSwitcher": "Switch well",
  "topbar.theme": "Theme",
  "topbar.theme.dark": "Dark",
  "topbar.theme.light": "Light",
  "topbar.theme.auto": "Auto",
  "topbar.locale": "Language",
  "topbar.lineage": "Data lineage",
  "topbar.lineage.adminOnly": "admin only",
  "topbar.lineage.on": "On",
  "topbar.lineage.off": "Off",
  "settings.title": "Settings",
  "settings.open": "Open settings",
  "settings.tenant": "Workspace",
  "settings.units": "Units",
  "settings.units.auto": "Auto (default)",
  "settings.units.hint": "Convert every measurement to your preferred unit system.",
  "settings.currency": "Currency",
  "settings.currency.native": "Native",
  "nav.collapse": "Collapse menu",
  "nav.expand": "Expand menu",
  "well.switcher.title": "Wells",
  "nav.wellExplorer": "Well Explorer",
  "nav.wellRecord": "Well record",
  "nav.dailyReport": "Daily report",
  "nav.timeNpt": "Time / NPT",
  "nav.costAfe": "Cost / AFE",
  "nav.bha": "BHA & Bit",
  "nav.tubulars": "Tubulars",
  "nav.mud": "Mud & fluids",
  "nav.hse": "HSE",
  "nav.surveys": "Surveys",
  "nav.pad": "Pad view",
  "nav.inbox": "Inbox",
  "nav.savedReports": "Saved reports",
  "nav.afe": "AFE",
  "nav.planner": "Planner",
  "planner.title": "Planner",
  "planner.subtitle": "Multi-track plan-vs-actual across the hierarchy.",
  "planner.level": "Level",
  "planner.level.corporate": "Corporate",
  "planner.level.regional": "Regional",
  "planner.level.local": "Local",
  "planner.level.fleet": "Fleet",
  "planner.level.pad": "Pad",
  "planner.level.well": "Well",
  "planner.level.section": "Section",
  "planner.zoom": "Zoom",
  "planner.zoom.day": "Day",
  "planner.zoom.week": "Week",
  "planner.zoom.month": "Month",
  "planner.zoom.quarter": "Quarter",
  "planner.zoom.year": "Year",
  "planner.autoZoom": "Auto-zoom",
  "planner.autoZoom.hint": "fit to data",
  "planner.show": "Show",
  "planner.pa.both": "Plan + Actual",
  "planner.pa.plan": "Plan only",
  "planner.pa.actual": "Actual only",
  "planner.legend.plan": "Plan",
  "planner.legend.actual": "Actual",
  "planner.legend.online": "Online",
  "planner.legend.offline": "Offline",
  "planner.legend.npt": "NPT",
  "planner.legend.today": "Today",
  "planner.cpeSpine": "Critical-path equipment (spine)",
  "planner.levelLocked": "Not available at your access scope",
  "planner.empty": "No planned or actual operations at this level.",
  "nav.catalogAdmin": "Catalog admin",
  "catadmin.title": "Catalog admin",
  "catadmin.subtitle": "A read-only preview of the Oxbo-M master catalogs the picklists bind to.",
  "catadmin.previewNote":
    "Preview only — catalogs are owned and edited in Oxbo-M. This screen browses them; it never creates, edits, or deletes values.",
  "catadmin.adminOnly": "This preview is available to catalog administrators only.",
  "catadmin.nav.aria": "Catalogs",
  "catadmin.nav.chrome": "Catalog admin",
  "catadmin.nav.find": "Find a catalog…",
  "catadmin.nav.noMatch": "No catalogs match.",
  "catadmin.group.operations": "Operations",
  "catadmin.group.cost": "Cost",
  "catadmin.group.equipment": "Equipment & tubulars",
  "catadmin.group.fluids": "Fluids & other",
  "catadmin.shape": "shape",
  "catadmin.version": "version",
  "catadmin.valueCount": "{n} values",
  "catadmin.seededFrom": "Seeded from",
  "catadmin.manage": "Manage in Oxbo-M",
  "catadmin.manageAria": "Open this catalog in the Oxbo-M admin (new tab)",
  "catadmin.noValues": "This catalog has no values.",
  "catadmin.col.code": "Code",
  "catadmin.col.label": "Label",
  "catadmin.col.parent": "Parent",
  "catadmin.col.aliases": "Aliases",
  "catadmin.col.active": "Active",
  "catadmin.on": "on",
  "catadmin.retired": "retired",
  "catadmin.govnote":
    "Shared masters are edited in Oxbo-M under a request → approval flow — this is what prevents value drift (the “four spellings of one account” failure). Oxbo-Z only reads them.",
  "explorer.title": "Well Explorer",
  "explorer.subtitle":
    "Your asset base, synced from OpenWells. Every well is a doorway into its record.",
  "explorer.search": "Search wells…",
  "explorer.filter.field": "Field",
  "explorer.filter.basin": "Basin",
  "explorer.filter.operator": "Operator",
  "explorer.filter.status": "Status",
  "explorer.filter.all": "All",
  "explorer.col.well": "Well",
  "explorer.col.uwi": "UWI / API",
  "explorer.col.field": "Field",
  "explorer.col.basin": "Basin",
  "explorer.col.operator": "Operator",
  "explorer.col.purpose": "Purpose",
  "explorer.col.status": "Status",
  "explorer.col.td": "Total depth",
  "explorer.map.placeholder": "Map view",
  "explorer.count": "{n} wells",
  "explorer.empty": "No wells match these filters.",
  "explorer.clearFilters": "Clear filters",
  "status.drilling": "Drilling",
  "status.completion": "Completion",
  "status.producing": "Producing",
  "status.suspended": "Suspended",
  "state.open": "Open",
  "state.closed": "Closed",
  "eventType.drilling": "Drilling",
  "eventType.completion": "Completion",
  "eventType.workover": "Workover",
  "eventType.intervention": "Intervention",
  "well.notSelected": "No well selected",
  "well.selectPrompt": "Pick a well from the Well Explorer to open its record.",
  "well.header.uwi": "UWI",
  "well.header.api": "API-14",
  "well.header.field": "Field",
  "well.header.operator": "Operator",
  "well.header.rig": "Rig",
  "well.header.formation": "Formation",
  "well.header.td": "Total depth",
  "well.header.status": "Status",
  "well.events": "Events / jobs",
  "well.event.select": "Event",
  "well.event.noEvents": "No events recorded for this well yet.",
  "well.doorway.dailyReport": "Daily report",
  "well.doorway.costAfe": "Cost & AFE",
  "well.doorway.timeNpt": "Time / NPT",
  "well.doorway.latestReport": "Latest report — Day {n}",
  "well.doorway.spent": "{pct} of AFE spent",
  "well.doorway.nptPct": "{pct} non-productive time",
  "well.doorway.open": "Open",
  "ddr.title": "Daily Drilling Report",
  "ddr.reportDate": "Report date",
  "ddr.day": "Day {n}",
  "ddr.selectReport": "Report",
  "ddr.noReports": "No daily reports on this event yet.",
  "ddr.create": "New report",
  "ddr.creating": "Creating…",
  "ddr.save": "Save report",
  "ddr.saving": "Saving…",
  "ddr.close": "Close report",
  "ddr.closed": "Report closed",
  "ddr.carryOver": "Carry over from previous",
  "ddr.carryOverHint": "Rolls yesterday's depths forward as a starting point.",
  "ddr.computed": "computed",
  "ddr.tab.general": "General",
  "ddr.tab.timeNpt": "Time / NPT",
  "ddr.tab.cost": "Cost",
  "ddr.tab.personnel": "Personnel",
  "ddr.tab.attachments": "Attachments",
  "ddr.kpi.day": "Day of well",
  "ddr.kpi.md": "MD current",
  "ddr.kpi.tvd": "TVD current",
  "ddr.kpi.rop": "ROP avg (24h)",
  "ddr.kpi.pct": "% complete",
  "ddr.kpi.cost": "Daily cost",
  "ddr.summary": "24-hr operations summary",
  "ddr.forecast": "24-hr forecast",
  "ddr.supervisor": "Supervisor",
  "ddr.field.mdCurrent": "MD current",
  "ddr.field.tvdCurrent": "TVD current",
  "ddr.personnel.placeholder": "Personnel-daily records load from Oxbo-P per crew and company.",
  "ddr.attachments.placeholder":
    "Attachments (morning reports, surveys, tickets) attach to this report.",
  "npt.title": "Time log / NPT",
  "npt.subtitle": "Non-productive time rolled up by cause — computed by Oxbo-P.",
  "npt.kpi.totalHours": "Total hours",
  "npt.kpi.nptHours": "NPT hours",
  "npt.kpi.nptPct": "NPT share",
  "npt.byCause": "NPT by cause",
  "npt.col.cause": "Cause",
  "npt.col.hours": "Hours",
  "npt.col.share": "Share",
  "npt.activityCodes": "Activity codes (from Oxbo-M)",
  "npt.noData": "No time-log data on this event yet.",
  "cost.title": "Cost & AFE",
  "cost.kpi.afeTotal": "AFE total (plan)",
  "cost.kpi.actual": "Actual to date",
  "cost.kpi.variance": "Variance",
  "cost.kpi.pctSpent": "% AFE spent",
  "cost.byPhase": "Plan vs. actual by phase",
  "cost.pivot": "Pivot",
  "cost.pivot.vendor": "By vendor",
  "cost.pivot.phase": "By phase",
  "cost.pivot.costClass": "By cost class",
  "cost.col.phase": "Phase",
  "cost.col.afe": "AFE plan",
  "cost.col.actual": "Actual",
  "cost.col.variance": "Variance",
  "cost.col.variancePct": "Variance %",
  "cost.legend.plan": "AFE plan",
  "cost.legend.actual": "Actual (on/under)",
  "cost.legend.over": "Actual (over)",
  "cost.col.key": "Item",
  "cost.col.total": "Total",
  "cost.total": "Total",
  "cost.noData": "No cost data on this event yet.",
  "inbox.title": "Inbox",
  "inbox.subtitle": "Cross-well report feed — triage reports needing attention.",
  "inbox.kpi.open": "Open reports",
  "inbox.kpi.closed": "Closed",
  "inbox.kpi.loaded": "Loaded",
  "inbox.kpi.wells": "Wells in view",
  "inbox.filter.state": "State",
  "inbox.filter.eventType": "Event type",
  "inbox.filter.well": "Well",
  "inbox.filter.dateRange": "Date range",
  "inbox.filter.scope": "Scope",
  "inbox.filter.all": "All",
  "inbox.filter.allWells": "All wells",
  "inbox.state.open": "Open",
  "inbox.state.closed": "Closed",
  "inbox.range.days": "Last {n} days",
  "inbox.range.all": "All time",
  "inbox.scope.all": "All",
  "inbox.scope.mine": "Mine",
  "inbox.col.state": "State",
  "inbox.col.well": "Well",
  "inbox.col.eventType": "Event type",
  "inbox.col.date": "Report date",
  "inbox.col.supervisor": "Supervisor",
  "inbox.col.age": "Age",
  "inbox.loadMore": "Load more",
  "inbox.clearFilters": "Clear filters",
  "inbox.refresh": "Refresh",
  "inbox.empty": "No reports match these filters.",
  "afe.title": "AFE Management",
  "afe.kpi.total": "AFE total",
  "afe.kpi.committed": "Committed",
  "afe.kpi.accrued": "Accrued",
  "afe.kpi.actual": "Actual / invoiced",
  "afe.kpi.variance": "Variance",
  "afe.sequence.title": "Ops program sequence",
  "afe.sequence.accruedOf": "accrued of",
  "afe.lines.title": "AFE lines",
  "afe.col.account": "Account",
  "afe.col.description": "Description",
  "afe.col.method": "Accrual method",
  "afe.col.treatment": "Treatment",
  "afe.col.budget": "Budget",
  "afe.col.committed": "Committed",
  "afe.col.accrued": "Accrued",
  "afe.col.actual": "Actual",
  "afe.col.variance": "Variance",
  "afe.col.status": "Status",
  "afe.method.by_quantity": "By quantity",
  "afe.method.lump": "Lump",
  "afe.method.pct_complete": "% complete",
  "afe.method.milestone": "Milestone",
  "afe.owner.service": "Service",
  "afe.scope.service": "Service-company view.",
  "afe.scope.serviceHint": "You see and self-serve only the lines scoped to your company.",
  "afe.devengado.term": "Devengado (accrued)",
  "afe.devengado.hint":
    "the portion of budget recognized as earned so far, independent of invoicing. The accrual method controls how each line accrues.",
  "afe.edit.method": "Re-point accrual method",
  "afe.noLines": "No AFE lines recorded for this step yet.",
  "saved.title": "Saved Views",
  "saved.subtitle":
    "Reusable filters, searches and pivots you've saved — open one to reapply it to the matching screen.",
  "saved.new": "Save a view",
  "saved.search": "Search saved queries…",
  "saved.filter.all": "All",
  "saved.filter.mine": "Mine",
  "saved.filter.public": "Public",
  "saved.col.name": "Name",
  "saved.col.description": "Description",
  "saved.col.opensIn": "Opens in",
  "saved.col.opensIn.title": "Which screen this saved view reapplies to",
  "saved.col.visibility": "Visibility",
  "saved.col.owner": "Owner",
  "saved.col.actions": "Actions",
  "saved.visibility.public": "Public",
  "saved.visibility.private": "Private",
  "saved.action.open": "Open",
  "saved.action.share": "Toggle sharing",
  "saved.action.export": "Export",
  "saved.action.delete": "Delete",
  "saved.empty": "No saved views match.",
  "saved.empty.clear": "Clear filters",
  "saved.noData": "You haven't saved any views yet. Use “Save a view” from any screen to capture its filters.",
  "saved.noDescription": "No description",
  "saved.exportSoon": "Export is coming soon.",
  "saved.confirmDelete": "Delete this saved view? This can't be undone.",
  "saved.dialog.title": "Save a view",
  "saved.dialog.name": "Name",
  "saved.dialog.namePlaceholder": "e.g. Open drilling reports — all wells",
  "saved.dialog.description": "Description",
  "saved.dialog.descriptionPlaceholder": "Optional — what this view is for",
  "saved.dialog.visibility": "Who can see it",
  "saved.dialog.visibility.private": "Private — only me",
  "saved.dialog.visibility.public": "Public — visible to org",
  "saved.dialog.opensIn": "Opens in",
  "saved.dialog.specNote": "Stored as a versioned filter spec ({v:1, target, params}) — not a document.",
  "saved.dialog.noSpecHint":
    "No view captured — save one from a screen (Inbox filters, a Cost pivot, …) to store its spec.",
  "saved.dialog.cancel": "Cancel",
  "saved.dialog.save": "Save view",
  "saved.dialog.saving": "Saving…",
  "saved.target.inbox": "Inbox",
  "saved.target.cost-afe": "Cost pivot",
  "saved.target.time-npt": "Time/NPT",
  "saved.target.surveys": "Surveys",
  "saved.target.bha": "BHA",
  "saved.target.tubulars": "Tubulars",
  "saved.target.mud": "Mud",
  "saved.target.hse": "HSE",
  "surveys.title": "Surveys",
  "surveys.kpi.currentMd": "Current MD",
  "surveys.kpi.currentTvd": "Current TVD",
  "surveys.kpi.maxInc": "Max inc",
  "surveys.kpi.maxDls": "Max DLS",
  "surveys.kpi.closure": "Closure / azimuth",
  "surveys.unit.dls": "°/100 ft",
  "surveys.col.md": "MD",
  "surveys.col.inc": "Inc°",
  "surveys.col.azi": "Azi°",
  "surveys.col.tvd": "TVD",
  "surveys.col.ns": "N/S",
  "surveys.col.ew": "E/W",
  "surveys.col.dls": "DLS °/100",
  "surveys.col.tool": "Tool",
  "surveys.col.date": "Date",
  "surveys.col.calc": "calc",
  "surveys.col.pending": "auto",
  "surveys.dls.warn": "DLS high",
  "surveys.dls.warnTitle": "Above the 1.5°/100 soft threshold",
  "surveys.paste.title": "Import stations",
  "surveys.paste.hint": "MD  Inc  Azi",
  "surveys.paste.note": "TVD / N-S / E-W / DLS are computed server-side on save.",
  "surveys.paste.placeholder": "17500\t88.6\t184.5\n17890\t89.1\t184.9",
  "surveys.paste.action": "Parse & append",
  "surveys.stations.title": "Survey stations",
  "surveys.stations.hint": "Raw entry: MD · Inc · Azi · Tool. Trajectory columns are computed — read-only.",
  "surveys.stations.empty": "No survey stations logged yet.",
  "surveys.compute.note":
    "Trajectory (TVD, N/S, E/W, DLS, closure) is computed by minimum curvature on the OXBO-x compute-server — not typed.",
  "surveys.plot.empty": "No computed trajectory yet — add stations to plot the wellpath.",
  "surveys.plot.wellpath": "Wellpath",
  "surveys.plot.dls": "DLS by station",
  "surveys.plot.plan": "Plan view (E vs N)",
  "surveys.plot.plan.sub": "true north up",
  "surveys.plot.plan.aria": "Plan view: East displacement against North displacement, surface to bottomhole.",
  "surveys.plot.section": "Vertical section vs TVD",
  "surveys.plot.section.sub": "horizontal displacement",
  "surveys.plot.section.aria":
    "Vertical section: horizontal displacement against true vertical depth, depth increasing downward.",
  "surveys.plot.dlsTitle": "DLS vs MD",
  "surveys.plot.dls.sub": "soft-warn ≥ 1.5",
  "surveys.plot.dls.aria": "Dogleg severity against measured depth, with a 1.5 soft-warn threshold line.",
  "surveys.axis.easting": "Easting",
  "surveys.axis.northing": "Northing",
  "surveys.axis.vsection": "Vertical section",
  "surveys.axis.tvd": "TVD",
  "surveys.axis.md": "MD",
  "surveys.axis.dls": "DLS (°/100)",
  "tub.title": "Tubulars",
  "tub.subtitle": "Casing / tubing running & pipe tally",
  "tub.tab.strings": "Strings",
  "tub.tab.tally": "Pipe Tally",
  "tub.addString": "Add string",
  "tub.addJoints": "Add joints",
  "tub.kpi.totalLength": "Total string length",
  "tub.kpi.shoeMd": "Shoe MD (active)",
  "tub.kpi.shoeTvd": "Shoe TVD (active)",
  "tub.kpi.jointsRun": "Joints run today",
  "tub.strings.caption":
    "Casing / liner program for this event · carried strings are read-only until pulled",
  "tub.col.string": "String",
  "tub.col.size": "Size (OD)",
  "tub.col.grade": "Grade",
  "tub.col.connection": "Connection",
  "tub.col.shoeMd": "Shoe MD",
  "tub.col.shoeTvd": "Shoe TVD",
  "tub.col.status": "Status",
  "tub.status.running": "Running",
  "tub.status.set": "Set",
  "tub.status.planned": "Planned",
  "tub.carried": "Carried",
  "tub.tally.caption": "Joint-by-joint tally · running string",
  "tub.tally.for": "Tally for",
  "tub.tally.pick": "Pick a string to view its pipe tally.",
  "tub.col.jt": "Jt#",
  "tub.col.length": "Length",
  "tub.col.cum": "Cumulative",
  "tub.col.od": "OD",
  "tub.col.serial": "Serial",
  "tub.tally.run": "Run",
  "tub.tally.runningJoint": "Running",
  "tub.schematic.title": "Wellbore schematic",
  "tub.schematic.subtitle": "Concentric casing strings to shoe depth · linear MD scale, 0 at top",
  "tub.schematic.bitDepth": "Current bit depth",
  "tub.schematic.openHole": "open hole",
  "tub.schematic.wellhead": "GL / wellhead",
  "tub.noData": "No tubular strings on this event yet.",
  "tub.noStrings": "No strings recorded — add the first casing/liner string.",
  "tub.noTally": "No joints tallied for this string yet.",
  "bha.title": "BHA / Bit",
  "bha.newRun": "New BHA run",
  "bha.kpi.rop": "On-bottom ROP",
  "bha.kpi.interval": "Drilled interval",
  "bha.kpi.bitHours": "Bit hours (current)",
  "bha.kpi.lastDull": "Last dull grade",
  "bha.runs": "BHA runs",
  "bha.col.run": "Run #",
  "bha.col.inMd": "In MD",
  "bha.col.outMd": "Out MD",
  "bha.col.hours": "Hours",
  "bha.col.bit": "Bit",
  "bha.col.rop": "ROP",
  "bha.col.components": "Components",
  "bha.inHole": "In hole",
  "bha.pooh": "POOH",
  "bha.runDetail": "Run detail",
  "bha.components": "Components (bottom-up)",
  "bha.comp.type": "Type",
  "bha.comp.od": "OD",
  "bha.comp.length": "Length",
  "bha.comp.vendor": "Vendor",
  "bha.bit": "Bit",
  "bha.bit.size": "Size / IADC",
  "bha.bit.jets": "Jets",
  "bha.bit.inOut": "In / Out depth",
  "bha.bit.hours": "Hours on bottom",
  "bha.bit.tfa": "TFA",
  "bha.dull": "Dull grade (IADC 8-point)",
  "bha.bitOps": "Bit operating parameters",
  "bha.op.md": "MD",
  "bha.op.rop": "ROP",
  "bha.op.wob": "WOB",
  "bha.op.rpm": "RPM",
  "bha.op.flow": "Flow rate",
  "bha.schematic": "BHA schematic — top of assembly → bit",
  "bha.ropVsDepth": "ROP vs depth — current run",
  "bha.selectRun": "Select a run to see its assembly, bit, and dull grade.",
  "bha.noData": "No BHA runs recorded on this event yet.",
  "bha.form.runNo": "Run #",
  "bha.form.bitType": "Bit type",
  "bha.form.vendor": "Vendor",
  "bha.form.save": "Save run",
  "mud.title": "Mud & fluids",
  "mud.subtitle": "Daily properties, products consumed, and system volumes.",
  "mud.noData": "No mud data on this report yet.",
  "mud.selectReport": "Report",
  "mud.kpi.currentMw": "Current MW",
  "mud.kpi.ecd": "ECD (computed)",
  "mud.kpi.dailyCost": "Daily mud cost",
  "mud.kpi.cumLosses": "Cumulative losses",
  "mud.section.properties": "Properties",
  "mud.section.properties.note": "today's checks",
  "mud.section.products": "Products",
  "mud.section.products.note": "consumed today",
  "mud.section.volumes": "Volumes",
  "mud.col.time": "Time",
  "mud.col.depth": "Depth",
  "mud.col.mw": "MW",
  "mud.col.pv": "PV",
  "mud.col.yp": "YP",
  "mud.col.ph": "pH",
  "mud.col.chlorides": "Cl⁻",
  "mud.col.product": "Product",
  "mud.col.qty": "Qty",
  "mud.col.unit": "Unit",
  "mud.col.unitCost": "Unit cost",
  "mud.col.total": "Total",
  "mud.vol.active": "Active",
  "mud.vol.reserve": "Reserve",
  "mud.vol.built": "Built today",
  "mud.vol.dumped": "Dumped today",
  "mud.vol.lossSurface": "Losses — surface",
  "mud.vol.lossDownhole": "Losses — downhole",
  "mud.chart.trend": "MW / PV / YP trend",
  "mud.chart.trend.sub": "vs report date",
  "mud.chart.losses": "Losses — cumulative",
  "mud.chart.cost": "Product cost breakdown",
  "mud.axis.reportDate": "Report date",
  "mud.axis.value": "Value",
  "mud.axis.losses": "Losses (cum.)",
  "mud.axis.cost": "Cost",
  "mud.axis.product": "Product",
  "mud.carried": "carried",
  "mud.noChecks": "No property checks logged for this report.",
  "mud.noProducts": "No products consumed on this report.",
  "mud.totalDailyCost": "Total daily mud cost",
  "mud.mwWarn": "MW outside program window — soft warning.",
  "hse.title": "HSE",
  "hse.subtitle": "Daily HSE record — LTI counters and checks computed by Oxbo-P.",
  "hse.kpi.daysSinceLti": "Days since LTI (total)",
  "hse.kpi.daysSinceLtiOperator": "Days since LTI (operator)",
  "hse.kpi.observations": "Observations this event",
  "hse.kpi.nearMisses": "Near-misses this event",
  "hse.kpi.drills": "Drills this event",
  "hse.day.title": "HSE day header",
  "hse.day.wellControl": "Well control",
  "hse.day.bopTest": "BOP test",
  "hse.day.koomey": "Koomey / accumulator check",
  "hse.day.checks": "Compliance checks",
  "hse.day.ridCards": "RID / STOP cards",
  "hse.day.jsa": "JSAs",
  "hse.day.toolbox": "Toolbox talks",
  "hse.day.drillsLogged": "Drills",
  "hse.day.people": "People & incidents",
  "hse.day.crewOnSite": "Crew on site",
  "hse.day.recordables": "Recordables",
  "hse.day.firstAids": "First aids",
  "hse.day.narrative": "HSE narrative",
  "hse.bop.pass": "Pass",
  "hse.bop.fail": "Fail",
  "hse.bop.na": "N/A",
  "hse.trail.title": "Daily HSE trail",
  "hse.trail.date": "Date",
  "hse.trail.bop": "BOP",
  "hse.trail.ridCards": "RID cards",
  "hse.trail.jsa": "JSAs",
  "hse.trail.toolbox": "Toolbox",
  "hse.trail.drills": "Drills",
  "hse.trail.recordables": "Recordables",
  "hse.trail.observations": "Observations",
  "hse.yes": "Yes",
  "hse.no": "No",
  "hse.noData": "No HSE record for this report yet.",
  "pad.title": "Pad view",
  "pad.wellCount": "{n} wells",
  "pad.kpi.totalCost": "Pad total cost",
  "pad.kpi.sharedCost": "Shared cost",
  "pad.kpi.allocated": "Allocated to wells",
  "pad.kpi.padDays": "Pad days",
  "pad.kpi.days": "days",
  "pad.kpi.rigUtil": "Rig utilization",
  "pad.costs": "Costs",
  "pad.time": "Time",
  "pad.sharedLines": "Shared / pad cost lines",
  "pad.noSharedLines": "No shared cost lines yet.",
  "pad.perWellRollup": "Per-well rollup",
  "pad.timeRollup": "Pad time rollup",
  "pad.viewSplit": "view split",
  "pad.rigUtilNote": "Rig utilization {pct} — online time over elapsed across the pad.",
  "pad.noData": "No rollup for this pad yet.",
  "pad.noTimeline": "No timeline for this pad yet.",
  "pad.col.costClass": "Cost class",
  "pad.col.description": "Description",
  "pad.col.vendor": "Vendor",
  "pad.col.amount": "Amount",
  "pad.col.allocMethod": "Allocation method",
  "pad.col.split": "Per-well split",
  "pad.col.well": "Well",
  "pad.col.share": "Share",
  "pad.col.allocated": "Allocated",
  "pad.col.directCost": "Direct cost",
  "pad.col.allocatedShared": "Allocated shared",
  "pad.col.total": "Total",
  "pad.col.pctOfPad": "% of pad",
  "pad.col.padTotal": "Pad total",
  "pad.col.daysOnWell": "Days on well",
  "pad.col.sequence": "Sequence",
  "pad.alloc.equal": "Equal split",
  "pad.alloc.wellCount": "By well count",
  "pad.alloc.footage": "By footage",
  "pad.alloc.rigDays": "By rig-days",
  "pad.alloc.afeWeight": "By AFE weight",
  "pad.alloc.manualPct": "Manual %",
  "pad.chart.costByWell": "Pad cost by well",
  "pad.chart.gantt": "Pad timeline",
  "pad.chart.date": "Date",
  "common.loading": "Loading…",
  "common.error": "Something went wrong.",
  "common.retry": "Retry",
  "common.export": "Export",
  "common.cancel": "Cancel",
  "common.na": "—",
  "common.of": "of",
};

const es: Catalog = {
  "app.name": "Oxbo-Z",
  "topbar.wellSwitcher": "Cambiar pozo",
  "topbar.theme": "Tema",
  "topbar.theme.dark": "Oscuro",
  "topbar.theme.light": "Claro",
  "topbar.theme.auto": "Auto",
  "topbar.locale": "Idioma",
  "topbar.lineage": "Linaje de datos",
  "topbar.lineage.adminOnly": "solo admin",
  "topbar.lineage.on": "Activado",
  "topbar.lineage.off": "Desactivado",
  "settings.title": "Ajustes",
  "settings.open": "Abrir ajustes",
  "settings.tenant": "Espacio de trabajo",
  "settings.units": "Unidades",
  "settings.units.auto": "Auto (predeterminado)",
  "settings.units.hint": "Convertí cada medición a tu sistema de unidades preferido.",
  "settings.currency": "Moneda",
  "settings.currency.native": "Nativa",
  "nav.collapse": "Contraer menú",
  "nav.expand": "Expandir menú",
  "well.switcher.title": "Pozos",
  "nav.wellExplorer": "Explorador de pozos",
  "nav.wellRecord": "Ficha del pozo",
  "nav.dailyReport": "Parte diario",
  "nav.timeNpt": "Tiempo / NPT",
  "nav.costAfe": "Costo / AFE",
  "nav.bha": "BHA y trépano",
  "nav.tubulars": "Tubulares",
  "nav.mud": "Lodo y fluidos",
  "nav.hse": "HSE",
  "nav.surveys": "Perfilajes",
  "nav.inbox": "Bandeja",
  "nav.pad": "Vista de pad",
  "nav.savedReports": "Reportes guardados",
  "nav.afe": "AFE",
  "nav.planner": "Planificador",
  "planner.title": "Planificador",
  "planner.subtitle": "Multi-pista plan-vs-real en toda la jerarquía.",
  "planner.level": "Nivel",
  "planner.level.corporate": "Corporativo",
  "planner.level.regional": "Regional",
  "planner.level.local": "Local",
  "planner.level.fleet": "Flota",
  "planner.level.pad": "Locación",
  "planner.level.well": "Pozo",
  "planner.level.section": "Sección",
  "planner.zoom": "Zoom",
  "planner.zoom.day": "Día",
  "planner.zoom.week": "Semana",
  "planner.zoom.month": "Mes",
  "planner.zoom.quarter": "Trimestre",
  "planner.zoom.year": "Año",
  "planner.autoZoom": "Auto-zoom",
  "planner.autoZoom.hint": "ajustar a los datos",
  "planner.show": "Mostrar",
  "planner.pa.both": "Plan + Real",
  "planner.pa.plan": "Solo plan",
  "planner.pa.actual": "Solo real",
  "planner.legend.plan": "Plan",
  "planner.legend.actual": "Real",
  "planner.legend.online": "En línea",
  "planner.legend.offline": "Fuera de línea",
  "planner.legend.npt": "TNP",
  "planner.legend.today": "Hoy",
  "planner.cpeSpine": "Equipo de ruta crítica (eje)",
  "planner.levelLocked": "No disponible en tu alcance de acceso",
  "planner.empty": "Sin operaciones planificadas ni reales en este nivel.",
  "nav.catalogAdmin": "Admin de catálogos",
  "catadmin.title": "Admin de catálogos",
  "catadmin.subtitle":
    "Vista de solo lectura de los catálogos maestros de Oxbo-M a los que se enlazan las listas.",
  "catadmin.previewNote":
    "Solo vista previa — los catálogos pertenecen y se editan en Oxbo-M. Esta pantalla los consulta; nunca crea, edita ni elimina valores.",
  "catadmin.adminOnly": "Esta vista previa está disponible solo para administradores de catálogos.",
  "catadmin.nav.aria": "Catálogos",
  "catadmin.nav.chrome": "Admin de catálogos",
  "catadmin.nav.find": "Buscar un catálogo…",
  "catadmin.nav.noMatch": "Ningún catálogo coincide.",
  "catadmin.group.operations": "Operaciones",
  "catadmin.group.cost": "Costos",
  "catadmin.group.equipment": "Equipos y tubulares",
  "catadmin.group.fluids": "Fluidos y otros",
  "catadmin.shape": "forma",
  "catadmin.version": "versión",
  "catadmin.valueCount": "{n} valores",
  "catadmin.seededFrom": "Sembrado desde",
  "catadmin.manage": "Gestionar en Oxbo-M",
  "catadmin.manageAria": "Abrir este catálogo en el admin de Oxbo-M (nueva pestaña)",
  "catadmin.noValues": "Este catálogo no tiene valores.",
  "catadmin.col.code": "Código",
  "catadmin.col.label": "Etiqueta",
  "catadmin.col.parent": "Padre",
  "catadmin.col.aliases": "Alias",
  "catadmin.col.active": "Activo",
  "catadmin.on": "activo",
  "catadmin.retired": "retirado",
  "catadmin.govnote":
    "Los maestros compartidos se editan en Oxbo-M mediante un flujo de solicitud → aprobación — así se evita la deriva de valores (la falla de las “cuatro grafías de una cuenta”). Oxbo-Z solo los lee.",
  "explorer.title": "Explorador de pozos",
  "explorer.subtitle":
    "Tu base de activos, sincronizada desde OpenWells. Cada pozo es una puerta a su ficha.",
  "explorer.search": "Buscar pozos…",
  "explorer.filter.field": "Yacimiento",
  "explorer.filter.basin": "Cuenca",
  "explorer.filter.operator": "Operadora",
  "explorer.filter.status": "Estado",
  "explorer.filter.all": "Todos",
  "explorer.col.well": "Pozo",
  "explorer.col.uwi": "UWI / API",
  "explorer.col.field": "Yacimiento",
  "explorer.col.basin": "Cuenca",
  "explorer.col.operator": "Operadora",
  "explorer.col.purpose": "Propósito",
  "explorer.col.status": "Estado",
  "explorer.col.td": "Profundidad",
  "explorer.map.placeholder": "Vista de mapa",
  "explorer.count": "{n} pozos",
  "explorer.empty": "Ningún pozo coincide con estos filtros.",
  "explorer.clearFilters": "Limpiar filtros",
  "status.drilling": "Perforación",
  "status.completion": "Terminación",
  "status.producing": "Producción",
  "status.suspended": "Suspendido",
  "state.open": "Abierto",
  "state.closed": "Cerrado",
  "eventType.drilling": "Perforación",
  "eventType.completion": "Terminación",
  "eventType.workover": "Reparación",
  "eventType.intervention": "Intervención",
  "well.notSelected": "Ningún pozo seleccionado",
  "well.selectPrompt": "Elegí un pozo en el Explorador para abrir su ficha.",
  "well.header.uwi": "UWI",
  "well.header.api": "API-14",
  "well.header.field": "Yacimiento",
  "well.header.operator": "Operadora",
  "well.header.rig": "Equipo",
  "well.header.formation": "Formación",
  "well.header.td": "Profundidad",
  "well.header.status": "Estado",
  "well.events": "Eventos / trabajos",
  "well.event.select": "Evento",
  "well.event.noEvents": "Aún no hay eventos registrados para este pozo.",
  "well.doorway.dailyReport": "Parte diario",
  "well.doorway.costAfe": "Costo y AFE",
  "well.doorway.timeNpt": "Tiempo / NPT",
  "well.doorway.latestReport": "Último parte — Día {n}",
  "well.doorway.spent": "{pct} del AFE consumido",
  "well.doorway.nptPct": "{pct} de tiempo no productivo",
  "well.doorway.open": "Abrir",
  "ddr.title": "Parte Diario de Perforación",
  "ddr.reportDate": "Fecha del parte",
  "ddr.day": "Día {n}",
  "ddr.selectReport": "Parte",
  "ddr.noReports": "Aún no hay partes diarios en este evento.",
  "ddr.create": "Nuevo parte",
  "ddr.creating": "Creando…",
  "ddr.save": "Guardar parte",
  "ddr.saving": "Guardando…",
  "ddr.close": "Cerrar parte",
  "ddr.closed": "Parte cerrado",
  "ddr.carryOver": "Arrastrar del anterior",
  "ddr.carryOverHint": "Traslada las profundidades de ayer como punto de partida.",
  "ddr.computed": "calculado",
  "ddr.tab.general": "General",
  "ddr.tab.timeNpt": "Tiempo / NPT",
  "ddr.tab.cost": "Costo",
  "ddr.tab.personnel": "Personal",
  "ddr.tab.attachments": "Adjuntos",
  "ddr.kpi.day": "Día del pozo",
  "ddr.kpi.md": "MD actual",
  "ddr.kpi.tvd": "TVD actual",
  "ddr.kpi.rop": "ROP prom. (24h)",
  "ddr.kpi.pct": "% completado",
  "ddr.kpi.cost": "Costo diario",
  "ddr.summary": "Resumen de operaciones 24 h",
  "ddr.forecast": "Pronóstico 24 h",
  "ddr.supervisor": "Supervisor",
  "ddr.field.mdCurrent": "MD actual",
  "ddr.field.tvdCurrent": "TVD actual",
  "ddr.personnel.placeholder":
    "Los registros de personal diario se cargan desde Oxbo-P por cuadrilla y empresa.",
  "ddr.attachments.placeholder":
    "Los adjuntos (partes de mañana, perfilajes, tickets) se asocian a este parte.",
  "npt.title": "Registro de tiempo / NPT",
  "npt.subtitle": "Tiempo no productivo consolidado por causa — calculado por Oxbo-P.",
  "npt.kpi.totalHours": "Horas totales",
  "npt.kpi.nptHours": "Horas NPT",
  "npt.kpi.nptPct": "Proporción NPT",
  "npt.byCause": "NPT por causa",
  "npt.col.cause": "Causa",
  "npt.col.hours": "Horas",
  "npt.col.share": "Proporción",
  "npt.activityCodes": "Códigos de actividad (desde Oxbo-M)",
  "npt.noData": "Aún no hay datos de tiempo en este evento.",
  "cost.title": "Costo y AFE",
  "cost.kpi.afeTotal": "AFE total (plan)",
  "cost.kpi.actual": "Real a la fecha",
  "cost.kpi.variance": "Variación",
  "cost.kpi.pctSpent": "% del AFE consumido",
  "cost.byPhase": "Plan vs. real por fase",
  "cost.pivot": "Pivote",
  "cost.pivot.vendor": "Por proveedor",
  "cost.pivot.phase": "Por fase",
  "cost.pivot.costClass": "Por clase de costo",
  "cost.col.phase": "Fase",
  "cost.col.afe": "Plan AFE",
  "cost.col.actual": "Real",
  "cost.col.variance": "Variación",
  "cost.col.variancePct": "Variación %",
  "cost.legend.plan": "Plan AFE",
  "cost.legend.actual": "Real (en/bajo)",
  "cost.legend.over": "Real (excedido)",
  "cost.col.key": "Ítem",
  "cost.col.total": "Total",
  "cost.total": "Total",
  "cost.noData": "Aún no hay datos de costo en este evento.",
  "inbox.title": "Bandeja",
  "inbox.subtitle": "Feed de partes de todos los pozos — priorizá los que requieren atención.",
  "inbox.kpi.open": "Partes abiertos",
  "inbox.kpi.closed": "Cerrados",
  "inbox.kpi.loaded": "Cargados",
  "inbox.kpi.wells": "Pozos en vista",
  "inbox.filter.state": "Estado",
  "inbox.filter.eventType": "Tipo de evento",
  "inbox.filter.well": "Pozo",
  "inbox.filter.dateRange": "Rango de fechas",
  "inbox.filter.scope": "Alcance",
  "inbox.filter.all": "Todos",
  "inbox.filter.allWells": "Todos los pozos",
  "inbox.state.open": "Abierto",
  "inbox.state.closed": "Cerrado",
  "inbox.range.days": "Últimos {n} días",
  "inbox.range.all": "Todo el período",
  "inbox.scope.all": "Todos",
  "inbox.scope.mine": "Míos",
  "inbox.col.state": "Estado",
  "inbox.col.well": "Pozo",
  "inbox.col.eventType": "Tipo de evento",
  "inbox.col.date": "Fecha del parte",
  "inbox.col.supervisor": "Supervisor",
  "inbox.col.age": "Antigüedad",
  "inbox.loadMore": "Cargar más",
  "inbox.clearFilters": "Limpiar filtros",
  "inbox.refresh": "Actualizar",
  "inbox.empty": "Ningún parte coincide con estos filtros.",
  "afe.title": "Gestión de AFE",
  "afe.kpi.total": "AFE total",
  "afe.kpi.committed": "Comprometido",
  "afe.kpi.accrued": "Devengado",
  "afe.kpi.actual": "Real / facturado",
  "afe.kpi.variance": "Variación",
  "afe.sequence.title": "Secuencia del programa",
  "afe.sequence.accruedOf": "devengado de",
  "afe.lines.title": "Líneas AFE",
  "afe.col.account": "Cuenta",
  "afe.col.description": "Descripción",
  "afe.col.method": "Método de devengo",
  "afe.col.treatment": "Tratamiento",
  "afe.col.budget": "Presupuesto",
  "afe.col.committed": "Comprometido",
  "afe.col.accrued": "Devengado",
  "afe.col.actual": "Real",
  "afe.col.variance": "Variación",
  "afe.col.status": "Estado",
  "afe.method.by_quantity": "Por cantidad",
  "afe.method.lump": "Total",
  "afe.method.pct_complete": "% avance",
  "afe.method.milestone": "Hito",
  "afe.owner.service": "Servicio",
  "afe.scope.service": "Vista de empresa de servicios.",
  "afe.scope.serviceHint": "Ve y autogestiona solo las líneas asignadas a su empresa.",
  "afe.devengado.term": "Devengado",
  "afe.devengado.hint":
    "la porción del presupuesto reconocida como ganada hasta ahora, independiente de la facturación. El método de devengo controla cómo devenga cada línea.",
  "afe.edit.method": "Reasignar método de devengo",
  "afe.noLines": "Aún no hay líneas AFE para este paso.",
  "saved.title": "Vistas guardadas",
  "saved.subtitle":
    "Filtros, búsquedas y tablas dinámicas reutilizables que guardaste — abre una para reaplicarla en su pantalla.",
  "saved.new": "Guardar vista",
  "saved.search": "Buscar consultas guardadas…",
  "saved.filter.all": "Todas",
  "saved.filter.mine": "Mías",
  "saved.filter.public": "Públicas",
  "saved.col.name": "Nombre",
  "saved.col.description": "Descripción",
  "saved.col.opensIn": "Abre en",
  "saved.col.opensIn.title": "En qué pantalla se reaplica esta vista guardada",
  "saved.col.visibility": "Visibilidad",
  "saved.col.owner": "Propietario",
  "saved.col.actions": "Acciones",
  "saved.visibility.public": "Pública",
  "saved.visibility.private": "Privada",
  "saved.action.open": "Abrir",
  "saved.action.share": "Alternar uso compartido",
  "saved.action.export": "Exportar",
  "saved.action.delete": "Eliminar",
  "saved.empty": "Ninguna vista guardada coincide.",
  "saved.empty.clear": "Limpiar filtros",
  "saved.noData":
    "Aún no guardaste ninguna vista. Usa «Guardar vista» desde cualquier pantalla para capturar sus filtros.",
  "saved.noDescription": "Sin descripción",
  "saved.exportSoon": "La exportación estará disponible pronto.",
  "saved.confirmDelete": "¿Eliminar esta vista guardada? No se puede deshacer.",
  "saved.dialog.title": "Guardar vista",
  "saved.dialog.name": "Nombre",
  "saved.dialog.namePlaceholder": "p. ej. Reportes de perforación abiertos — todos los pozos",
  "saved.dialog.description": "Descripción",
  "saved.dialog.descriptionPlaceholder": "Opcional — para qué sirve esta vista",
  "saved.dialog.visibility": "Quién puede verla",
  "saved.dialog.visibility.private": "Privada — solo yo",
  "saved.dialog.visibility.public": "Pública — visible para la organización",
  "saved.dialog.opensIn": "Abre en",
  "saved.dialog.specNote":
    "Se guarda como una especificación de filtro versionada ({v:1, target, params}) — no como un documento.",
  "saved.dialog.noSpecHint":
    "No se capturó ninguna vista — guarda una desde una pantalla (filtros de Bandeja, una tabla de Costos…) para almacenar su especificación.",
  "saved.dialog.cancel": "Cancelar",
  "saved.dialog.save": "Guardar vista",
  "saved.dialog.saving": "Guardando…",
  "saved.target.inbox": "Bandeja",
  "saved.target.cost-afe": "Tabla de costos",
  "saved.target.time-npt": "Tiempo/NPT",
  "saved.target.surveys": "Surveys",
  "saved.target.bha": "BHA",
  "saved.target.tubulars": "Tubulares",
  "saved.target.mud": "Lodo",
  "saved.target.hse": "HSE",
  "surveys.title": "Perfilajes",
  "surveys.kpi.currentMd": "MD actual",
  "surveys.kpi.currentTvd": "TVD actual",
  "surveys.kpi.maxInc": "Inc. máx.",
  "surveys.kpi.maxDls": "DLS máx.",
  "surveys.kpi.closure": "Cierre / azimut",
  "surveys.unit.dls": "°/100 ft",
  "surveys.col.md": "MD",
  "surveys.col.inc": "Inc°",
  "surveys.col.azi": "Azi°",
  "surveys.col.tvd": "TVD",
  "surveys.col.ns": "N/S",
  "surveys.col.ew": "E/O",
  "surveys.col.dls": "DLS °/100",
  "surveys.col.tool": "Herramienta",
  "surveys.col.date": "Fecha",
  "surveys.col.calc": "calc",
  "surveys.col.pending": "auto",
  "surveys.dls.warn": "DLS alto",
  "surveys.dls.warnTitle": "Supera el umbral leve de 1,5°/100",
  "surveys.paste.title": "Importar estaciones",
  "surveys.paste.hint": "MD  Inc  Azi",
  "surveys.paste.note": "TVD / N-S / E-O / DLS se calculan en el servidor al guardar.",
  "surveys.paste.placeholder": "17500\t88.6\t184.5\n17890\t89.1\t184.9",
  "surveys.paste.action": "Analizar y agregar",
  "surveys.stations.title": "Estaciones de perfilaje",
  "surveys.stations.hint":
    "Ingreso crudo: MD · Inc · Azi · Herramienta. Las columnas de trayectoria son calculadas — solo lectura.",
  "surveys.stations.empty": "Aún no hay estaciones de perfilaje.",
  "surveys.compute.note":
    "La trayectoria (TVD, N/S, E/O, DLS, cierre) se calcula por curvatura mínima en el compute-server de OXBO-x — no se tipea.",
  "surveys.plot.empty": "Sin trayectoria calculada — agregá estaciones para graficar el pozo.",
  "surveys.plot.wellpath": "Trayectoria",
  "surveys.plot.dls": "DLS por estación",
  "surveys.plot.plan": "Vista en planta (E vs N)",
  "surveys.plot.plan.sub": "norte hacia arriba",
  "surveys.plot.plan.aria": "Vista en planta: desplazamiento Este contra Norte, de superficie a fondo.",
  "surveys.plot.section": "Sección vertical vs TVD",
  "surveys.plot.section.sub": "desplazamiento horizontal",
  "surveys.plot.section.aria":
    "Sección vertical: desplazamiento horizontal contra profundidad vertical verdadera, profundidad creciente hacia abajo.",
  "surveys.plot.dlsTitle": "DLS vs MD",
  "surveys.plot.dls.sub": "aviso leve ≥ 1,5",
  "surveys.plot.dls.aria": "Severidad de pata de perro contra profundidad medida, con línea de aviso leve en 1,5.",
  "surveys.axis.easting": "Este",
  "surveys.axis.northing": "Norte",
  "surveys.axis.vsection": "Sección vertical",
  "surveys.axis.tvd": "TVD",
  "surveys.axis.md": "MD",
  "surveys.axis.dls": "DLS (°/100)",
  "tub.title": "Tubulares",
  "tub.subtitle": "Bajada de casing / tubing y conteo de tubería",
  "tub.tab.strings": "Cañerías",
  "tub.tab.tally": "Conteo de tubería",
  "tub.addString": "Agregar cañería",
  "tub.addJoints": "Agregar juntas",
  "tub.kpi.totalLength": "Longitud total de cañería",
  "tub.kpi.shoeMd": "Zapato MD (activo)",
  "tub.kpi.shoeTvd": "Zapato TVD (activo)",
  "tub.kpi.jointsRun": "Juntas bajadas hoy",
  "tub.strings.caption":
    "Programa de casing / liner del evento · las cañerías arrastradas son de solo lectura hasta que se extraigan",
  "tub.col.string": "Cañería",
  "tub.col.size": "Diámetro (OD)",
  "tub.col.grade": "Grado",
  "tub.col.connection": "Conexión",
  "tub.col.shoeMd": "Zapato MD",
  "tub.col.shoeTvd": "Zapato TVD",
  "tub.col.status": "Estado",
  "tub.status.running": "Bajando",
  "tub.status.set": "Asentada",
  "tub.status.planned": "Planificada",
  "tub.carried": "Arrastrada",
  "tub.tally.caption": "Conteo junta por junta · cañería en bajada",
  "tub.tally.for": "Conteo de",
  "tub.tally.pick": "Elegí una cañería para ver su conteo.",
  "tub.col.jt": "Junta",
  "tub.col.length": "Longitud",
  "tub.col.cum": "Acumulado",
  "tub.col.od": "OD",
  "tub.col.serial": "Serie",
  "tub.tally.run": "Bajada",
  "tub.tally.runningJoint": "Bajando",
  "tub.schematic.title": "Esquema del pozo",
  "tub.schematic.subtitle": "Cañerías concéntricas hasta el zapato · escala MD lineal, 0 arriba",
  "tub.schematic.bitDepth": "Profundidad de trépano actual",
  "tub.schematic.openHole": "pozo abierto",
  "tub.schematic.wellhead": "Nivel / boca de pozo",
  "tub.noData": "Aún no hay cañerías en este evento.",
  "tub.noStrings": "Sin cañerías registradas — agregá la primera cañería de casing/liner.",
  "tub.noTally": "Aún no hay juntas contadas para esta cañería.",
  "bha.title": "BHA / Trépano",
  "bha.newRun": "Nueva carrera de BHA",
  "bha.kpi.rop": "ROP en fondo",
  "bha.kpi.interval": "Intervalo perforado",
  "bha.kpi.bitHours": "Horas de trépano (actual)",
  "bha.kpi.lastDull": "Último desgaste",
  "bha.runs": "Carreras de BHA",
  "bha.col.run": "Carrera #",
  "bha.col.inMd": "MD entrada",
  "bha.col.outMd": "MD salida",
  "bha.col.hours": "Horas",
  "bha.col.bit": "Trépano",
  "bha.col.rop": "ROP",
  "bha.col.components": "Componentes",
  "bha.inHole": "En pozo",
  "bha.pooh": "Fuera de pozo",
  "bha.runDetail": "Detalle de carrera",
  "bha.components": "Componentes (de fondo a superficie)",
  "bha.comp.type": "Tipo",
  "bha.comp.od": "DE",
  "bha.comp.length": "Longitud",
  "bha.comp.vendor": "Proveedor",
  "bha.bit": "Trépano",
  "bha.bit.size": "Tamaño / IADC",
  "bha.bit.jets": "Toberas",
  "bha.bit.inOut": "Profundidad entrada / salida",
  "bha.bit.hours": "Horas en fondo",
  "bha.bit.tfa": "TFA",
  "bha.dull": "Desgaste (IADC 8 puntos)",
  "bha.bitOps": "Parámetros de perforación",
  "bha.op.md": "MD",
  "bha.op.rop": "ROP",
  "bha.op.wob": "WOB",
  "bha.op.rpm": "RPM",
  "bha.op.flow": "Caudal",
  "bha.schematic": "Esquema de BHA — tope del conjunto → trépano",
  "bha.ropVsDepth": "ROP vs profundidad — carrera actual",
  "bha.selectRun": "Seleccione una carrera para ver su conjunto, trépano y desgaste.",
  "bha.noData": "Aún no hay carreras de BHA registradas en este evento.",
  "bha.form.runNo": "Carrera #",
  "bha.form.bitType": "Tipo de trépano",
  "bha.form.vendor": "Proveedor",
  "bha.form.save": "Guardar carrera",
  "mud.title": "Lodo y fluidos",
  "mud.subtitle": "Propiedades diarias, productos consumidos y volúmenes del sistema.",
  "mud.noData": "Aún no hay datos de lodo en este parte.",
  "mud.selectReport": "Parte",
  "mud.kpi.currentMw": "Densidad actual",
  "mud.kpi.ecd": "ECD (calculado)",
  "mud.kpi.dailyCost": "Costo de lodo diario",
  "mud.kpi.cumLosses": "Pérdidas acumuladas",
  "mud.section.properties": "Propiedades",
  "mud.section.properties.note": "chequeos de hoy",
  "mud.section.products": "Productos",
  "mud.section.products.note": "consumidos hoy",
  "mud.section.volumes": "Volúmenes",
  "mud.col.time": "Hora",
  "mud.col.depth": "Profundidad",
  "mud.col.mw": "Densidad",
  "mud.col.pv": "VP",
  "mud.col.yp": "PC",
  "mud.col.ph": "pH",
  "mud.col.chlorides": "Cl⁻",
  "mud.col.product": "Producto",
  "mud.col.qty": "Cant.",
  "mud.col.unit": "Unidad",
  "mud.col.unitCost": "Costo unit.",
  "mud.col.total": "Total",
  "mud.vol.active": "Activo",
  "mud.vol.reserve": "Reserva",
  "mud.vol.built": "Preparado hoy",
  "mud.vol.dumped": "Descartado hoy",
  "mud.vol.lossSurface": "Pérdidas — superficie",
  "mud.vol.lossDownhole": "Pérdidas — fondo",
  "mud.chart.trend": "Tendencia densidad / VP / PC",
  "mud.chart.trend.sub": "vs fecha del parte",
  "mud.chart.losses": "Pérdidas — acumuladas",
  "mud.chart.cost": "Desglose de costo por producto",
  "mud.axis.reportDate": "Fecha del parte",
  "mud.axis.value": "Valor",
  "mud.axis.losses": "Pérdidas (acum.)",
  "mud.axis.cost": "Costo",
  "mud.axis.product": "Producto",
  "mud.carried": "arrastrado",
  "mud.noChecks": "No hay chequeos registrados en este parte.",
  "mud.noProducts": "No hay productos consumidos en este parte.",
  "mud.totalDailyCost": "Costo total de lodo diario",
  "mud.mwWarn": "Densidad fuera de la ventana del programa — advertencia leve.",
  "hse.title": "HSE",
  "hse.subtitle": "Registro diario de HSE — contadores de LTI y controles calculados por Oxbo-P.",
  "hse.kpi.daysSinceLti": "Días sin LTI (total)",
  "hse.kpi.daysSinceLtiOperator": "Días sin LTI (operadora)",
  "hse.kpi.observations": "Observaciones en el evento",
  "hse.kpi.nearMisses": "Cuasi-accidentes en el evento",
  "hse.kpi.drills": "Simulacros en el evento",
  "hse.day.title": "Encabezado HSE del día",
  "hse.day.wellControl": "Control de pozo",
  "hse.day.bopTest": "Prueba de BOP",
  "hse.day.koomey": "Control Koomey / acumulador",
  "hse.day.checks": "Controles de cumplimiento",
  "hse.day.ridCards": "Tarjetas RID / STOP",
  "hse.day.jsa": "ATS",
  "hse.day.toolbox": "Charlas de seguridad",
  "hse.day.drillsLogged": "Simulacros",
  "hse.day.people": "Personal e incidentes",
  "hse.day.crewOnSite": "Dotación en sitio",
  "hse.day.recordables": "Registrables",
  "hse.day.firstAids": "Primeros auxilios",
  "hse.day.narrative": "Relato HSE",
  "hse.bop.pass": "OK",
  "hse.bop.fail": "Falla",
  "hse.bop.na": "N/D",
  "hse.trail.title": "Historial HSE diario",
  "hse.trail.date": "Fecha",
  "hse.trail.bop": "BOP",
  "hse.trail.ridCards": "Tarjetas RID",
  "hse.trail.jsa": "ATS",
  "hse.trail.toolbox": "Charlas",
  "hse.trail.drills": "Simulacros",
  "hse.trail.recordables": "Registrables",
  "hse.trail.observations": "Observaciones",
  "hse.yes": "Sí",
  "hse.no": "No",
  "hse.noData": "Aún no hay registro HSE para este parte.",
  "pad.title": "Vista de pad",
  "pad.wellCount": "{n} pozos",
  "pad.kpi.totalCost": "Costo total del pad",
  "pad.kpi.sharedCost": "Costo compartido",
  "pad.kpi.allocated": "Asignado a pozos",
  "pad.kpi.padDays": "Días de pad",
  "pad.kpi.days": "días",
  "pad.kpi.rigUtil": "Utilización de equipo",
  "pad.costs": "Costos",
  "pad.time": "Tiempo",
  "pad.sharedLines": "Líneas de costo compartido / pad",
  "pad.noSharedLines": "Aún no hay líneas de costo compartido.",
  "pad.perWellRollup": "Resumen por pozo",
  "pad.timeRollup": "Resumen de tiempo del pad",
  "pad.viewSplit": "ver reparto",
  "pad.rigUtilNote": "Utilización de equipo {pct} — tiempo en línea sobre transcurrido en el pad.",
  "pad.noData": "Aún no hay resumen para este pad.",
  "pad.noTimeline": "Aún no hay cronograma para este pad.",
  "pad.col.costClass": "Clase de costo",
  "pad.col.description": "Descripción",
  "pad.col.vendor": "Proveedor",
  "pad.col.amount": "Monto",
  "pad.col.allocMethod": "Método de asignación",
  "pad.col.split": "Reparto por pozo",
  "pad.col.well": "Pozo",
  "pad.col.share": "Participación",
  "pad.col.allocated": "Asignado",
  "pad.col.directCost": "Costo directo",
  "pad.col.allocatedShared": "Compartido asignado",
  "pad.col.total": "Total",
  "pad.col.pctOfPad": "% del pad",
  "pad.col.padTotal": "Total del pad",
  "pad.col.daysOnWell": "Días en pozo",
  "pad.col.sequence": "Secuencia",
  "pad.alloc.equal": "Reparto igual",
  "pad.alloc.wellCount": "Por número de pozos",
  "pad.alloc.footage": "Por metraje",
  "pad.alloc.rigDays": "Por días de equipo",
  "pad.alloc.afeWeight": "Por peso de AFE",
  "pad.alloc.manualPct": "% manual",
  "pad.chart.costByWell": "Costo del pad por pozo",
  "pad.chart.gantt": "Cronograma del pad",
  "pad.chart.date": "Fecha",
  "common.loading": "Cargando…",
  "common.error": "Algo salió mal.",
  "common.retry": "Reintentar",
  "common.export": "Exportar",
  "common.cancel": "Cancelar",
  "common.na": "—",
  "common.of": "de",
};

const ptBR: Catalog = {
  "app.name": "Oxbo-Z",
  "topbar.wellSwitcher": "Trocar poço",
  "topbar.theme": "Tema",
  "topbar.theme.dark": "Escuro",
  "topbar.theme.light": "Claro",
  "topbar.theme.auto": "Auto",
  "topbar.locale": "Idioma",
  "topbar.lineage": "Linhagem de dados",
  "topbar.lineage.adminOnly": "somente admin",
  "topbar.lineage.on": "Ligado",
  "topbar.lineage.off": "Desligado",
  "settings.title": "Configurações",
  "settings.open": "Abrir configurações",
  "settings.tenant": "Espaço de trabalho",
  "settings.units": "Unidades",
  "settings.units.auto": "Auto (padrão)",
  "settings.units.hint": "Converta cada medição para o seu sistema de unidades preferido.",
  "settings.currency": "Moeda",
  "settings.currency.native": "Nativa",
  "nav.collapse": "Recolher menu",
  "nav.expand": "Expandir menu",
  "well.switcher.title": "Poços",
  "nav.wellExplorer": "Explorador de poços",
  "nav.wellRecord": "Ficha do poço",
  "nav.dailyReport": "Boletim diário",
  "nav.timeNpt": "Tempo / NPT",
  "nav.costAfe": "Custo / AFE",
  "nav.bha": "BHA e broca",
  "nav.tubulars": "Tubulares",
  "nav.mud": "Fluidos",
  "nav.hse": "SMS",
  "nav.surveys": "Perfilagens",
  "nav.inbox": "Caixa de entrada",
  "nav.pad": "Visão do pad",
  "nav.savedReports": "Relatórios salvos",
  "nav.afe": "AFE",
  "nav.planner": "Planejador",
  "planner.title": "Planejador",
  "planner.subtitle": "Multipista plano-vs-real em toda a hierarquia.",
  "planner.level": "Nível",
  "planner.level.corporate": "Corporativo",
  "planner.level.regional": "Regional",
  "planner.level.local": "Local",
  "planner.level.fleet": "Frota",
  "planner.level.pad": "Locação",
  "planner.level.well": "Poço",
  "planner.level.section": "Seção",
  "planner.zoom": "Zoom",
  "planner.zoom.day": "Dia",
  "planner.zoom.week": "Semana",
  "planner.zoom.month": "Mês",
  "planner.zoom.quarter": "Trimestre",
  "planner.zoom.year": "Ano",
  "planner.autoZoom": "Auto-zoom",
  "planner.autoZoom.hint": "ajustar aos dados",
  "planner.show": "Mostrar",
  "planner.pa.both": "Plano + Real",
  "planner.pa.plan": "Somente plano",
  "planner.pa.actual": "Somente real",
  "planner.legend.plan": "Plano",
  "planner.legend.actual": "Real",
  "planner.legend.online": "Online",
  "planner.legend.offline": "Offline",
  "planner.legend.npt": "TNP",
  "planner.legend.today": "Hoje",
  "planner.cpeSpine": "Equipamento de caminho crítico (eixo)",
  "planner.levelLocked": "Indisponível no seu escopo de acesso",
  "planner.empty": "Sem operações planejadas ou reais neste nível.",
  "nav.catalogAdmin": "Admin de catálogos",
  "catadmin.title": "Admin de catálogos",
  "catadmin.subtitle":
    "Uma prévia somente leitura dos catálogos mestres do Oxbo-M aos quais as listas se vinculam.",
  "catadmin.previewNote":
    "Somente prévia — os catálogos pertencem e são editados no Oxbo-M. Esta tela os consulta; nunca cria, edita nem exclui valores.",
  "catadmin.adminOnly": "Esta prévia está disponível apenas para administradores de catálogos.",
  "catadmin.nav.aria": "Catálogos",
  "catadmin.nav.chrome": "Admin de catálogos",
  "catadmin.nav.find": "Encontrar um catálogo…",
  "catadmin.nav.noMatch": "Nenhum catálogo corresponde.",
  "catadmin.group.operations": "Operações",
  "catadmin.group.cost": "Custos",
  "catadmin.group.equipment": "Equipamentos e tubulares",
  "catadmin.group.fluids": "Fluidos e outros",
  "catadmin.shape": "forma",
  "catadmin.version": "versão",
  "catadmin.valueCount": "{n} valores",
  "catadmin.seededFrom": "Semeado de",
  "catadmin.manage": "Gerenciar no Oxbo-M",
  "catadmin.manageAria": "Abrir este catálogo no admin do Oxbo-M (nova aba)",
  "catadmin.noValues": "Este catálogo não tem valores.",
  "catadmin.col.code": "Código",
  "catadmin.col.label": "Rótulo",
  "catadmin.col.parent": "Pai",
  "catadmin.col.aliases": "Aliases",
  "catadmin.col.active": "Ativo",
  "catadmin.on": "ativo",
  "catadmin.retired": "aposentado",
  "catadmin.govnote":
    "Os mestres compartilhados são editados no Oxbo-M por um fluxo de solicitação → aprovação — é isso que evita a deriva de valores (a falha das “quatro grafias de uma conta”). O Oxbo-Z apenas os lê.",
  "explorer.title": "Explorador de poços",
  "explorer.subtitle":
    "Sua base de ativos, sincronizada do OpenWells. Cada poço é uma porta para sua ficha.",
  "explorer.search": "Buscar poços…",
  "explorer.filter.field": "Campo",
  "explorer.filter.basin": "Bacia",
  "explorer.filter.operator": "Operadora",
  "explorer.filter.status": "Situação",
  "explorer.filter.all": "Todos",
  "explorer.col.well": "Poço",
  "explorer.col.uwi": "UWI / API",
  "explorer.col.field": "Campo",
  "explorer.col.basin": "Bacia",
  "explorer.col.operator": "Operadora",
  "explorer.col.purpose": "Finalidade",
  "explorer.col.status": "Situação",
  "explorer.col.td": "Profundidade",
  "explorer.map.placeholder": "Vista do mapa",
  "explorer.count": "{n} poços",
  "explorer.empty": "Nenhum poço corresponde a estes filtros.",
  "explorer.clearFilters": "Limpar filtros",
  "status.drilling": "Perfuração",
  "status.completion": "Completação",
  "status.producing": "Produção",
  "status.suspended": "Suspenso",
  "state.open": "Aberto",
  "state.closed": "Fechado",
  "eventType.drilling": "Perfuração",
  "eventType.completion": "Completação",
  "eventType.workover": "Workover",
  "eventType.intervention": "Intervenção",
  "well.notSelected": "Nenhum poço selecionado",
  "well.selectPrompt": "Escolha um poço no Explorador para abrir sua ficha.",
  "well.header.uwi": "UWI",
  "well.header.api": "API-14",
  "well.header.field": "Campo",
  "well.header.operator": "Operadora",
  "well.header.rig": "Sonda",
  "well.header.formation": "Formação",
  "well.header.td": "Profundidade",
  "well.header.status": "Situação",
  "well.events": "Eventos / trabalhos",
  "well.event.select": "Evento",
  "well.event.noEvents": "Ainda não há eventos registrados para este poço.",
  "well.doorway.dailyReport": "Boletim diário",
  "well.doorway.costAfe": "Custo e AFE",
  "well.doorway.timeNpt": "Tempo / NPT",
  "well.doorway.latestReport": "Último boletim — Dia {n}",
  "well.doorway.spent": "{pct} do AFE gasto",
  "well.doorway.nptPct": "{pct} de tempo não produtivo",
  "well.doorway.open": "Abrir",
  "ddr.title": "Boletim Diário de Perfuração",
  "ddr.reportDate": "Data do boletim",
  "ddr.day": "Dia {n}",
  "ddr.selectReport": "Boletim",
  "ddr.noReports": "Ainda não há boletins diários neste evento.",
  "ddr.create": "Novo boletim",
  "ddr.creating": "Criando…",
  "ddr.save": "Salvar boletim",
  "ddr.saving": "Salvando…",
  "ddr.close": "Fechar boletim",
  "ddr.closed": "Boletim fechado",
  "ddr.carryOver": "Herdar do anterior",
  "ddr.carryOverHint": "Traz as profundidades de ontem como ponto de partida.",
  "ddr.computed": "calculado",
  "ddr.tab.general": "Geral",
  "ddr.tab.timeNpt": "Tempo / NPT",
  "ddr.tab.cost": "Custo",
  "ddr.tab.personnel": "Pessoal",
  "ddr.tab.attachments": "Anexos",
  "ddr.kpi.day": "Dia do poço",
  "ddr.kpi.md": "MD atual",
  "ddr.kpi.tvd": "TVD atual",
  "ddr.kpi.rop": "ROP méd. (24h)",
  "ddr.kpi.pct": "% concluído",
  "ddr.kpi.cost": "Custo diário",
  "ddr.summary": "Resumo de operações 24 h",
  "ddr.forecast": "Previsão 24 h",
  "ddr.supervisor": "Supervisor",
  "ddr.field.mdCurrent": "MD atual",
  "ddr.field.tvdCurrent": "TVD atual",
  "ddr.personnel.placeholder":
    "Os registros de pessoal diário carregam do Oxbo-P por turma e empresa.",
  "ddr.attachments.placeholder":
    "Os anexos (boletins da manhã, perfis, tickets) vinculam-se a este boletim.",
  "npt.title": "Registro de tempo / NPT",
  "npt.subtitle": "Tempo não produtivo consolidado por causa — calculado pelo Oxbo-P.",
  "npt.kpi.totalHours": "Horas totais",
  "npt.kpi.nptHours": "Horas NPT",
  "npt.kpi.nptPct": "Proporção NPT",
  "npt.byCause": "NPT por causa",
  "npt.col.cause": "Causa",
  "npt.col.hours": "Horas",
  "npt.col.share": "Proporção",
  "npt.activityCodes": "Códigos de atividade (do Oxbo-M)",
  "npt.noData": "Ainda não há dados de tempo neste evento.",
  "cost.title": "Custo e AFE",
  "cost.kpi.afeTotal": "AFE total (plano)",
  "cost.kpi.actual": "Real até a data",
  "cost.kpi.variance": "Variação",
  "cost.kpi.pctSpent": "% do AFE gasto",
  "cost.byPhase": "Plano vs. real por fase",
  "cost.pivot": "Pivô",
  "cost.pivot.vendor": "Por fornecedor",
  "cost.pivot.phase": "Por fase",
  "cost.pivot.costClass": "Por classe de custo",
  "cost.col.phase": "Fase",
  "cost.col.afe": "Plano AFE",
  "cost.col.actual": "Real",
  "cost.col.variance": "Variação",
  "cost.col.variancePct": "Variação %",
  "cost.legend.plan": "Plano AFE",
  "cost.legend.actual": "Real (em/abaixo)",
  "cost.legend.over": "Real (acima)",
  "cost.col.key": "Item",
  "cost.col.total": "Total",
  "cost.total": "Total",
  "cost.noData": "Ainda não há dados de custo neste evento.",
  "inbox.title": "Caixa de entrada",
  "inbox.subtitle": "Feed de boletins de todos os poços — priorize os que precisam de atenção.",
  "inbox.kpi.open": "Boletins abertos",
  "inbox.kpi.closed": "Fechados",
  "inbox.kpi.loaded": "Carregados",
  "inbox.kpi.wells": "Poços em vista",
  "inbox.filter.state": "Estado",
  "inbox.filter.eventType": "Tipo de evento",
  "inbox.filter.well": "Poço",
  "inbox.filter.dateRange": "Intervalo de datas",
  "inbox.filter.scope": "Escopo",
  "inbox.filter.all": "Todos",
  "inbox.filter.allWells": "Todos os poços",
  "inbox.state.open": "Aberto",
  "inbox.state.closed": "Fechado",
  "inbox.range.days": "Últimos {n} dias",
  "inbox.range.all": "Todo o período",
  "inbox.scope.all": "Todos",
  "inbox.scope.mine": "Meus",
  "inbox.col.state": "Estado",
  "inbox.col.well": "Poço",
  "inbox.col.eventType": "Tipo de evento",
  "inbox.col.date": "Data do boletim",
  "inbox.col.supervisor": "Supervisor",
  "inbox.col.age": "Idade",
  "inbox.loadMore": "Carregar mais",
  "inbox.clearFilters": "Limpar filtros",
  "inbox.refresh": "Atualizar",
  "inbox.empty": "Nenhum boletim corresponde a estes filtros.",
  "afe.title": "Gestão de AFE",
  "afe.kpi.total": "AFE total",
  "afe.kpi.committed": "Comprometido",
  "afe.kpi.accrued": "Apropriado",
  "afe.kpi.actual": "Real / faturado",
  "afe.kpi.variance": "Variação",
  "afe.sequence.title": "Sequência do programa",
  "afe.sequence.accruedOf": "apropriado de",
  "afe.lines.title": "Linhas AFE",
  "afe.col.account": "Conta",
  "afe.col.description": "Descrição",
  "afe.col.method": "Método de apropriação",
  "afe.col.treatment": "Tratamento",
  "afe.col.budget": "Orçamento",
  "afe.col.committed": "Comprometido",
  "afe.col.accrued": "Apropriado",
  "afe.col.actual": "Real",
  "afe.col.variance": "Variação",
  "afe.col.status": "Status",
  "afe.method.by_quantity": "Por quantidade",
  "afe.method.lump": "Total",
  "afe.method.pct_complete": "% concluído",
  "afe.method.milestone": "Marco",
  "afe.owner.service": "Serviço",
  "afe.scope.service": "Visão da empresa de serviços.",
  "afe.scope.serviceHint": "Você vê e autogerencia apenas as linhas atribuídas à sua empresa.",
  "afe.devengado.term": "Apropriado (devengado)",
  "afe.devengado.hint":
    "a parcela do orçamento reconhecida como incorrida até agora, independente do faturamento. O método de apropriação controla como cada linha apropria.",
  "afe.edit.method": "Redefinir método de apropriação",
  "afe.noLines": "Ainda não há linhas AFE para esta etapa.",
  "saved.title": "Visões salvas",
  "saved.subtitle":
    "Filtros, buscas e tabelas dinâmicas reutilizáveis que você salvou — abra uma para reaplicá-la na tela correspondente.",
  "saved.new": "Salvar visão",
  "saved.search": "Buscar consultas salvas…",
  "saved.filter.all": "Todas",
  "saved.filter.mine": "Minhas",
  "saved.filter.public": "Públicas",
  "saved.col.name": "Nome",
  "saved.col.description": "Descrição",
  "saved.col.opensIn": "Abre em",
  "saved.col.opensIn.title": "Em qual tela esta visão salva é reaplicada",
  "saved.col.visibility": "Visibilidade",
  "saved.col.owner": "Proprietário",
  "saved.col.actions": "Ações",
  "saved.visibility.public": "Pública",
  "saved.visibility.private": "Privada",
  "saved.action.open": "Abrir",
  "saved.action.share": "Alternar compartilhamento",
  "saved.action.export": "Exportar",
  "saved.action.delete": "Excluir",
  "saved.empty": "Nenhuma visão salva corresponde.",
  "saved.empty.clear": "Limpar filtros",
  "saved.noData":
    "Você ainda não salvou nenhuma visão. Use “Salvar visão” em qualquer tela para capturar seus filtros.",
  "saved.noDescription": "Sem descrição",
  "saved.exportSoon": "A exportação estará disponível em breve.",
  "saved.confirmDelete": "Excluir esta visão salva? Isso não pode ser desfeito.",
  "saved.dialog.title": "Salvar visão",
  "saved.dialog.name": "Nome",
  "saved.dialog.namePlaceholder": "ex.: Relatórios de perfuração abertos — todos os poços",
  "saved.dialog.description": "Descrição",
  "saved.dialog.descriptionPlaceholder": "Opcional — para que serve esta visão",
  "saved.dialog.visibility": "Quem pode ver",
  "saved.dialog.visibility.private": "Privada — só eu",
  "saved.dialog.visibility.public": "Pública — visível para a organização",
  "saved.dialog.opensIn": "Abre em",
  "saved.dialog.specNote":
    "Armazenada como uma especificação de filtro versionada ({v:1, target, params}) — não como um documento.",
  "saved.dialog.noSpecHint":
    "Nenhuma visão capturada — salve uma a partir de uma tela (filtros da Caixa, uma tabela de Custos…) para armazenar sua especificação.",
  "saved.dialog.cancel": "Cancelar",
  "saved.dialog.save": "Salvar visão",
  "saved.dialog.saving": "Salvando…",
  "saved.target.inbox": "Caixa",
  "saved.target.cost-afe": "Tabela de custos",
  "saved.target.time-npt": "Tempo/NPT",
  "saved.target.surveys": "Surveys",
  "saved.target.bha": "BHA",
  "saved.target.tubulars": "Tubulares",
  "saved.target.mud": "Fluidos",
  "saved.target.hse": "HSE",
  "surveys.title": "Perfilagens",
  "surveys.kpi.currentMd": "MD atual",
  "surveys.kpi.currentTvd": "TVD atual",
  "surveys.kpi.maxInc": "Inc. máx.",
  "surveys.kpi.maxDls": "DLS máx.",
  "surveys.kpi.closure": "Fechamento / azimute",
  "surveys.unit.dls": "°/100 ft",
  "surveys.col.md": "MD",
  "surveys.col.inc": "Inc°",
  "surveys.col.azi": "Azi°",
  "surveys.col.tvd": "TVD",
  "surveys.col.ns": "N/S",
  "surveys.col.ew": "L/O",
  "surveys.col.dls": "DLS °/100",
  "surveys.col.tool": "Ferramenta",
  "surveys.col.date": "Data",
  "surveys.col.calc": "calc",
  "surveys.col.pending": "auto",
  "surveys.dls.warn": "DLS alto",
  "surveys.dls.warnTitle": "Acima do limite leve de 1,5°/100",
  "surveys.paste.title": "Importar estações",
  "surveys.paste.hint": "MD  Inc  Azi",
  "surveys.paste.note": "TVD / N-S / L-O / DLS são calculados no servidor ao salvar.",
  "surveys.paste.placeholder": "17500\t88.6\t184.5\n17890\t89.1\t184.9",
  "surveys.paste.action": "Analisar e anexar",
  "surveys.stations.title": "Estações de perfilagem",
  "surveys.stations.hint":
    "Entrada bruta: MD · Inc · Azi · Ferramenta. As colunas de trajetória são calculadas — somente leitura.",
  "surveys.stations.empty": "Ainda não há estações de perfilagem.",
  "surveys.compute.note":
    "A trajetória (TVD, N/S, L/O, DLS, fechamento) é calculada por curvatura mínima no compute-server do OXBO-x — não é digitada.",
  "surveys.plot.empty": "Sem trajetória calculada — adicione estações para plotar o poço.",
  "surveys.plot.wellpath": "Trajetória",
  "surveys.plot.dls": "DLS por estação",
  "surveys.plot.plan": "Vista em planta (L vs N)",
  "surveys.plot.plan.sub": "norte para cima",
  "surveys.plot.plan.aria": "Vista em planta: deslocamento Leste contra Norte, da superfície ao fundo.",
  "surveys.plot.section": "Seção vertical vs TVD",
  "surveys.plot.section.sub": "deslocamento horizontal",
  "surveys.plot.section.aria":
    "Seção vertical: deslocamento horizontal contra profundidade vertical verdadeira, profundidade crescente para baixo.",
  "surveys.plot.dlsTitle": "DLS vs MD",
  "surveys.plot.dls.sub": "aviso leve ≥ 1,5",
  "surveys.plot.dls.aria": "Severidade de pata de cachorro contra profundidade medida, com linha de aviso leve em 1,5.",
  "surveys.axis.easting": "Leste",
  "surveys.axis.northing": "Norte",
  "surveys.axis.vsection": "Seção vertical",
  "surveys.axis.tvd": "TVD",
  "surveys.axis.md": "MD",
  "surveys.axis.dls": "DLS (°/100)",
  "tub.title": "Tubulares",
  "tub.subtitle": "Descida de revestimento / tubing e contagem de tubos",
  "tub.tab.strings": "Colunas",
  "tub.tab.tally": "Contagem de tubos",
  "tub.addString": "Adicionar coluna",
  "tub.addJoints": "Adicionar juntas",
  "tub.kpi.totalLength": "Comprimento total da coluna",
  "tub.kpi.shoeMd": "Sapata MD (ativa)",
  "tub.kpi.shoeTvd": "Sapata TVD (ativa)",
  "tub.kpi.jointsRun": "Juntas descidas hoje",
  "tub.strings.caption":
    "Programa de revestimento / liner do evento · colunas herdadas são somente leitura até serem retiradas",
  "tub.col.string": "Coluna",
  "tub.col.size": "Diâmetro (OD)",
  "tub.col.grade": "Grau",
  "tub.col.connection": "Conexão",
  "tub.col.shoeMd": "Sapata MD",
  "tub.col.shoeTvd": "Sapata TVD",
  "tub.col.status": "Estado",
  "tub.status.running": "Descendo",
  "tub.status.set": "Assentada",
  "tub.status.planned": "Planejada",
  "tub.carried": "Herdada",
  "tub.tally.caption": "Contagem junta a junta · coluna em descida",
  "tub.tally.for": "Contagem de",
  "tub.tally.pick": "Escolha uma coluna para ver sua contagem.",
  "tub.col.jt": "Junta",
  "tub.col.length": "Comprimento",
  "tub.col.cum": "Acumulado",
  "tub.col.od": "OD",
  "tub.col.serial": "Série",
  "tub.tally.run": "Descida",
  "tub.tally.runningJoint": "Descendo",
  "tub.schematic.title": "Esquema do poço",
  "tub.schematic.subtitle": "Colunas concêntricas até a sapata · escala MD linear, 0 no topo",
  "tub.schematic.bitDepth": "Profundidade da broca atual",
  "tub.schematic.openHole": "poço aberto",
  "tub.schematic.wellhead": "Nível / boca do poço",
  "tub.noData": "Ainda não há colunas neste evento.",
  "tub.noStrings": "Nenhuma coluna registrada — adicione a primeira coluna de revestimento/liner.",
  "tub.noTally": "Ainda não há juntas contadas para esta coluna.",
  "bha.title": "BHA / Broca",
  "bha.newRun": "Nova corrida de BHA",
  "bha.kpi.rop": "ROP no fundo",
  "bha.kpi.interval": "Intervalo perfurado",
  "bha.kpi.bitHours": "Horas de broca (atual)",
  "bha.kpi.lastDull": "Último desgaste",
  "bha.runs": "Corridas de BHA",
  "bha.col.run": "Corrida #",
  "bha.col.inMd": "MD entrada",
  "bha.col.outMd": "MD saída",
  "bha.col.hours": "Horas",
  "bha.col.bit": "Broca",
  "bha.col.rop": "ROP",
  "bha.col.components": "Componentes",
  "bha.inHole": "No poço",
  "bha.pooh": "Fora do poço",
  "bha.runDetail": "Detalhe da corrida",
  "bha.components": "Componentes (de fundo a superfície)",
  "bha.comp.type": "Tipo",
  "bha.comp.od": "DE",
  "bha.comp.length": "Comprimento",
  "bha.comp.vendor": "Fornecedor",
  "bha.bit": "Broca",
  "bha.bit.size": "Tamanho / IADC",
  "bha.bit.jets": "Jatos",
  "bha.bit.inOut": "Profundidade entrada / saída",
  "bha.bit.hours": "Horas no fundo",
  "bha.bit.tfa": "TFA",
  "bha.dull": "Desgaste (IADC 8 pontos)",
  "bha.bitOps": "Parâmetros de perfuração",
  "bha.op.md": "MD",
  "bha.op.rop": "ROP",
  "bha.op.wob": "WOB",
  "bha.op.rpm": "RPM",
  "bha.op.flow": "Vazão",
  "bha.schematic": "Esquema de BHA — topo do conjunto → broca",
  "bha.ropVsDepth": "ROP vs profundidade — corrida atual",
  "bha.selectRun": "Selecione uma corrida para ver seu conjunto, broca e desgaste.",
  "bha.noData": "Ainda não há corridas de BHA registradas neste evento.",
  "bha.form.runNo": "Corrida #",
  "bha.form.bitType": "Tipo de broca",
  "bha.form.vendor": "Fornecedor",
  "bha.form.save": "Salvar corrida",
  "mud.title": "Fluidos",
  "mud.subtitle": "Propriedades diárias, produtos consumidos e volumes do sistema.",
  "mud.noData": "Ainda não há dados de fluido neste boletim.",
  "mud.selectReport": "Boletim",
  "mud.kpi.currentMw": "Densidade atual",
  "mud.kpi.ecd": "ECD (calculado)",
  "mud.kpi.dailyCost": "Custo diário de fluido",
  "mud.kpi.cumLosses": "Perdas acumuladas",
  "mud.section.properties": "Propriedades",
  "mud.section.properties.note": "verificações de hoje",
  "mud.section.products": "Produtos",
  "mud.section.products.note": "consumidos hoje",
  "mud.section.volumes": "Volumes",
  "mud.col.time": "Hora",
  "mud.col.depth": "Profundidade",
  "mud.col.mw": "Densidade",
  "mud.col.pv": "VP",
  "mud.col.yp": "LE",
  "mud.col.ph": "pH",
  "mud.col.chlorides": "Cl⁻",
  "mud.col.product": "Produto",
  "mud.col.qty": "Qtd.",
  "mud.col.unit": "Unidade",
  "mud.col.unitCost": "Custo unit.",
  "mud.col.total": "Total",
  "mud.vol.active": "Ativo",
  "mud.vol.reserve": "Reserva",
  "mud.vol.built": "Preparado hoje",
  "mud.vol.dumped": "Descartado hoje",
  "mud.vol.lossSurface": "Perdas — superfície",
  "mud.vol.lossDownhole": "Perdas — fundo",
  "mud.chart.trend": "Tendência densidade / VP / LE",
  "mud.chart.trend.sub": "vs data do boletim",
  "mud.chart.losses": "Perdas — acumuladas",
  "mud.chart.cost": "Detalhamento de custo por produto",
  "mud.axis.reportDate": "Data do boletim",
  "mud.axis.value": "Valor",
  "mud.axis.losses": "Perdas (acum.)",
  "mud.axis.cost": "Custo",
  "mud.axis.product": "Produto",
  "mud.carried": "arrastado",
  "mud.noChecks": "Nenhuma verificação registrada neste boletim.",
  "mud.noProducts": "Nenhum produto consumido neste boletim.",
  "mud.totalDailyCost": "Custo total diário de fluido",
  "mud.mwWarn": "Densidade fora da janela do programa — aviso leve.",
  "hse.title": "HSE",
  "hse.subtitle":
    "Registro diário de HSE — contadores de LTI e verificações calculados pelo Oxbo-P.",
  "hse.kpi.daysSinceLti": "Dias sem LTI (total)",
  "hse.kpi.daysSinceLtiOperator": "Dias sem LTI (operadora)",
  "hse.kpi.observations": "Observações no evento",
  "hse.kpi.nearMisses": "Quase-acidentes no evento",
  "hse.kpi.drills": "Simulados no evento",
  "hse.day.title": "Cabeçalho HSE do dia",
  "hse.day.wellControl": "Controle de poço",
  "hse.day.bopTest": "Teste de BOP",
  "hse.day.koomey": "Verificação Koomey / acumulador",
  "hse.day.checks": "Verificações de conformidade",
  "hse.day.ridCards": "Cartões RID / STOP",
  "hse.day.jsa": "APR",
  "hse.day.toolbox": "Diálogos de segurança",
  "hse.day.drillsLogged": "Simulados",
  "hse.day.people": "Pessoas e incidentes",
  "hse.day.crewOnSite": "Equipe no local",
  "hse.day.recordables": "Registráveis",
  "hse.day.firstAids": "Primeiros socorros",
  "hse.day.narrative": "Relato HSE",
  "hse.bop.pass": "OK",
  "hse.bop.fail": "Falha",
  "hse.bop.na": "N/D",
  "hse.trail.title": "Histórico HSE diário",
  "hse.trail.date": "Data",
  "hse.trail.bop": "BOP",
  "hse.trail.ridCards": "Cartões RID",
  "hse.trail.jsa": "APR",
  "hse.trail.toolbox": "Diálogos",
  "hse.trail.drills": "Simulados",
  "hse.trail.recordables": "Registráveis",
  "hse.trail.observations": "Observações",
  "hse.yes": "Sim",
  "hse.no": "Não",
  "hse.noData": "Ainda não há registro HSE para este relatório.",
  "pad.title": "Visão do pad",
  "pad.wellCount": "{n} poços",
  "pad.kpi.totalCost": "Custo total do pad",
  "pad.kpi.sharedCost": "Custo compartilhado",
  "pad.kpi.allocated": "Alocado aos poços",
  "pad.kpi.padDays": "Dias de pad",
  "pad.kpi.days": "dias",
  "pad.kpi.rigUtil": "Utilização da sonda",
  "pad.costs": "Custos",
  "pad.time": "Tempo",
  "pad.sharedLines": "Linhas de custo compartilhado / pad",
  "pad.noSharedLines": "Ainda não há linhas de custo compartilhado.",
  "pad.perWellRollup": "Resumo por poço",
  "pad.timeRollup": "Resumo de tempo do pad",
  "pad.viewSplit": "ver rateio",
  "pad.rigUtilNote": "Utilização da sonda {pct} — tempo on-line sobre decorrido no pad.",
  "pad.noData": "Ainda não há resumo para este pad.",
  "pad.noTimeline": "Ainda não há cronograma para este pad.",
  "pad.col.costClass": "Classe de custo",
  "pad.col.description": "Descrição",
  "pad.col.vendor": "Fornecedor",
  "pad.col.amount": "Valor",
  "pad.col.allocMethod": "Método de rateio",
  "pad.col.split": "Rateio por poço",
  "pad.col.well": "Poço",
  "pad.col.share": "Participação",
  "pad.col.allocated": "Alocado",
  "pad.col.directCost": "Custo direto",
  "pad.col.allocatedShared": "Compartilhado alocado",
  "pad.col.total": "Total",
  "pad.col.pctOfPad": "% do pad",
  "pad.col.padTotal": "Total do pad",
  "pad.col.daysOnWell": "Dias no poço",
  "pad.col.sequence": "Sequência",
  "pad.alloc.equal": "Rateio igual",
  "pad.alloc.wellCount": "Por número de poços",
  "pad.alloc.footage": "Por metragem",
  "pad.alloc.rigDays": "Por dias de sonda",
  "pad.alloc.afeWeight": "Por peso de AFE",
  "pad.alloc.manualPct": "% manual",
  "pad.chart.costByWell": "Custo do pad por poço",
  "pad.chart.gantt": "Cronograma do pad",
  "pad.chart.date": "Data",
  "common.loading": "Carregando…",
  "common.error": "Algo deu errado.",
  "common.retry": "Tentar novamente",
  "common.export": "Exportar",
  "common.cancel": "Cancelar",
  "common.na": "—",
  "common.of": "de",
};

export const CATALOGS: Record<Locale, Catalog> = {
  en,
  es,
  "pt-BR": ptBR,
};

/** Resolve a message, with EN fallback and simple {name} interpolation. */
export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  const raw = CATALOGS[locale][key] ?? en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_m, name: string) =>
    name in vars ? String(vars[name]) : `{${name}}`,
  );
}
